import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { apiService } from "../services/api";
import { toast } from "sonner";

const stripHtml = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    const text = doc.body.textContent || "";
    return text.replace(/\u00a0/g, " ").trim();
};

const decodeHtml = (value) => {
    if (!value) {
        return "";
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
};

const buildLocalDateTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) {
        return null;
    }
    const trimmedTime = String(timeValue).trim();
    if (!trimmedTime) {
        return null;
    }
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(trimmedTime)) {
        const normalized = trimmedTime.replace(" ", "T");
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const match = trimmedTime.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
        return null;
    }
    const hours = match[1].padStart(2, "0");
    const minutes = match[2];
    const combined = `${dateValue}T${hours}:${minutes}`;
    const parsed = new Date(combined);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Zusätzliche GEMA-Angaben, die die Tarife abfragen (Backend: gema_logic.KUBU_PREFILL_RULES).
// Ja/Nein-Felder liegen im Formular als "ja"/"nein", im Backend als Boolean.
const GEMA_EXTRA_DEFAULTS = {
    gema_pausen_ohne_musik_min: "0",
    gema_sponsoring: "nein",
    gema_pausenmusik: "NEIN",
    gema_tv_wiedergabe: "NEIN",
    gema_tontraegereinlage: "nein",
};
const GEMA_JA_NEIN_FIELDS = ["gema_sponsoring", "gema_tontraegereinlage"];

const gemaExtrasFromEvent = (event) => {
    const result = { ...GEMA_EXTRA_DEFAULTS };
    Object.keys(GEMA_EXTRA_DEFAULTS).forEach((field) => {
        const value = event?.[field];
        if (value === null || value === undefined || value === "") {
            return;
        }
        result[field] = GEMA_JA_NEIN_FIELDS.includes(field) ? (value ? "ja" : "nein") : String(value);
    });
    return result;
};

const gemaExtrasToPayload = (formData) => {
    const payload = {};
    Object.keys(GEMA_EXTRA_DEFAULTS).forEach((field) => {
        const value = formData[field];
        if (!formData.gema_relevant || value === "" || value === null || value === undefined) {
            payload[field] = null;
        } else if (GEMA_JA_NEIN_FIELDS.includes(field)) {
            payload[field] = value === "ja";
        } else if (field === "gema_pausen_ohne_musik_min") {
            payload[field] = Number(String(value).replace(",", "."));
        } else {
            payload[field] = value;
        }
    });
    return payload;
};

// Vergleichswert für Datum/Uhrzeit, um Änderungen an bereits bei der GEMA gemeldeten Events zu erkennen.
const gemaScheduleSignature = (source) => {
    const entries = Array.isArray(source?.event_dates) && source.event_dates.length > 0
        ? source.event_dates
        : [{ date: source?.date, time: source?.time, end_time: source?.end_time }];
    const pickTime = (value) => {
        const matches = String(value || "").match(/\d{1,2}:\d{2}/g);
        return matches ? matches[matches.length - 1].padStart(5, "0") : "";
    };
    return JSON.stringify(entries.map((entry) => [
        String(entry?.date || "").slice(0, 10),
        pickTime(entry?.time),
        pickTime(entry?.end_time),
    ]));
};

const normalizeTimeInputValue = (value) => {
    if (!value) {
        return "";
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return "";
    }
    const fullDateTimeMatch = trimmed.match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2})/);
    if (fullDateTimeMatch) {
        return fullDateTimeMatch[1];
    }
    const timeMatch = trimmed.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);
    if (timeMatch) {
        return timeMatch[1];
    }
    return trimmed;
};

const normalizeDateTimeLocalInputValue = (value, fallbackDate = "") => {
    if (!value) {
        return "";
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return "";
    }
    const fullDateTimeMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    if (fullDateTimeMatch) {
        return `${fullDateTimeMatch[1]}T${fullDateTimeMatch[2]}`;
    }
    const timeMatch = trimmed.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);
    if (timeMatch && fallbackDate) {
        return `${fallbackDate}T${timeMatch[1].padStart(5, "0")}`;
    }
    return trimmed;
};

const normalizeNumericInputValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};

const normalizeHtml = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    return doc.body.innerHTML.replace(/&nbsp;/g, " ");
};

const escapeHtml = (value) => {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
};

const sanitizeEditorUrl = (value) => {
    if (!value) {
        return "";
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return "";
    }
    const lower = trimmed.toLowerCase();
    if (
        lower.startsWith("http://") ||
        lower.startsWith("https://") ||
        lower.startsWith("mailto:") ||
        lower.startsWith("tel:") ||
        lower.startsWith("/")
    ) {
        return trimmed;
    }
    if (trimmed.startsWith("//")) {
        return `https:${trimmed}`;
    }
    // Any other "scheme:" prefix (data:, blob:, file:, javascript:, ...) is unsupported here.
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        return "";
    }
    return `https://${trimmed}`;
};

const PASTE_ALLOWED_STYLE_PROPS = new Set(["text-align", "font-weight", "font-style", "text-decoration"]);
const PASTE_ALLOWED_CLASS_TAGS = new Set(["HR"]);

const sanitizeStyleAttributeValue = (value) => {
    if (!value) {
        return "";
    }
    const kept = value
        .split(";")
        .map((declaration) => declaration.trim())
        .filter(Boolean)
        .filter((declaration) => {
            const prop = declaration.split(":")[0]?.trim().toLowerCase();
            return prop && PASTE_ALLOWED_STYLE_PROPS.has(prop);
        });
    return kept.join("; ");
};

const sanitizeDescriptionHtml = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    doc.querySelectorAll("script, iframe, object, embed, meta, style, link, xml").forEach((node) => node.remove());

    const walk = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const attributes = Array.from(node.attributes);
        attributes.forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value || "";
            if (name.startsWith("on")) {
                node.removeAttribute(attribute.name);
                return;
            }
            if (name === "href" || name === "src") {
                const safeUrl = sanitizeEditorUrl(value);
                if (!safeUrl) {
                    node.removeAttribute(attribute.name);
                } else {
                    node.setAttribute(attribute.name, safeUrl);
                }
                return;
            }
            if (name === "class") {
                if (!PASTE_ALLOWED_CLASS_TAGS.has(node.tagName)) {
                    node.removeAttribute(attribute.name);
                }
                return;
            }
            if (name === "style") {
                const safeStyle = sanitizeStyleAttributeValue(value);
                if (!safeStyle) {
                    node.removeAttribute(attribute.name);
                } else {
                    node.setAttribute(attribute.name, safeStyle);
                }
                return;
            }
            if (name === "lang" || name === "xml:lang") {
                node.removeAttribute(attribute.name);
                return;
            }
            if (name === "id" && /^docs-internal-guid/i.test(value)) {
                node.removeAttribute(attribute.name);
                return;
            }
            if (value.toLowerCase().includes("javascript:")) {
                node.removeAttribute(attribute.name);
            }
        });
        Array.from(node.childNodes).forEach(walk);
    };

    Array.from(doc.body.childNodes).forEach(walk);
    doc.querySelectorAll("img:not([src])").forEach((node) => node.remove());
    return doc.body.innerHTML.replace(/&nbsp;/g, " ");
};

const WP_SEPARATOR_MARKER = "__WP_SEPARATOR__";

const replaceSeparatorMarkersForEditor = (value) => {
    if (!value) {
        return "";
    }
    return value
        .replace(new RegExp(`<p>\\s*${WP_SEPARATOR_MARKER}\\s*<\\/p>`, "gi"), "<hr class=\"wp-block-separator has-alpha-channel-opacity\" />")
        .replace(new RegExp(WP_SEPARATOR_MARKER, "gi"), "<hr class=\"wp-block-separator has-alpha-channel-opacity\" />");
};

const replaceSeparatorForStorage = (value) => {
    if (!value) {
        return "";
    }
    return value.replace(/<hr\b[^>]*>/gi, `<p>${WP_SEPARATOR_MARKER}</p>`);
};

const resolveImageUrl = (url) => {
    if (!url) {
        return "";
    }
    if (url.startsWith("http")) {
        return url;
    }
    if (process.env.REACT_APP_BACKEND_URL) {
        return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
};

const normalizeEditorLinks = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    const anchors = doc.querySelectorAll("a[href]");
    anchors.forEach((anchor) => {
        const href = anchor.getAttribute("href") || "";
        const trimmed = href.trim();
        if (!trimmed) {
            return;
        }
        const lower = trimmed.toLowerCase();
        if (lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("mailto:") || lower.startsWith("tel:")) {
            return;
        }
        if (trimmed.startsWith("//")) {
            anchor.setAttribute("href", `https:${trimmed}`);
            return;
        }
        anchor.setAttribute("href", `https://${trimmed}`);
    });
    return doc.body.innerHTML;
};

const isMeaningfulEditorHtml = (value) => {
    if (!value) {
        return false;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    if (stripHtml(doc.body.innerHTML)) {
        return true;
    }
    return Boolean(doc.body.querySelector("hr, table, ul, ol, li"));
};

const normalizeEditorMarkup = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    const removableBlocks = doc.body.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, blockquote");

    removableBlocks.forEach((node) => {
        const hasStructuredContent = node.querySelector("img, table, hr, ul, ol, li, iframe, video, audio");
        if (hasStructuredContent) {
            return;
        }
        const normalizedInner = (node.innerHTML || "")
            .replace(/&nbsp;/gi, "")
            .replace(/<br\s*\/?>/gi, "")
            .replace(/\s+/g, "");
        if (!normalizedInner) {
            node.remove();
        }
    });

    let html = doc.body.innerHTML.replace(/&nbsp;/g, " ");
    html = html.replace(/(<br\s*\/?>\s*){3,}/gi, "<br /><br />");
    return html.trim();
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit"
    }).format(parsed);
};

const GEMA_EVENT_TYPES = [
    {
        value: "TYPE_CONCERT",
        title: "Konzert",
        subtext: "Die Band steht im Mittelpunkt.",
        tariff: "U-K",
        setlistRequired: true
    },
    {
        value: "TYPE_PARTY",
        title: "Party / DJ",
        subtext: "Konservenmusik zum Feiern.",
        tariff: "M-V",
        setlistRequired: false
    },
    {
        value: "TYPE_SPORT",
        title: "Sport / Yoga",
        subtext: "Musik zur sportlichen Begleitung.",
        tariff: "M-SP",
        setlistRequired: false
    },
    {
        value: "TYPE_LIVE_ENT",
        title: "Live-Hintergrund",
        subtext: "Live-Musik, aber nicht als Hauptgrund des Besuchs.",
        tariff: "U-V",
        setlistRequired: true
    },
    {
        value: "TYPE_STREET",
        title: "Straßenfest",
        subtext: "Open-Air ohne festen Sitzplatz.",
        tariff: "U-ST",
        setlistRequired: true
    },
    {
        value: "TYPE_BG",
        title: "Hintergrundmusik",
        subtext: "Leise Beschallung ohne Tanzfokus.",
        tariff: "M-U",
        setlistRequired: false
    }
];

const GEMA_ROOM_OPTIONS = [
    "Großer Veranstaltungsraum",
    "Kleiner Austellungsraum",
    "Großer Austellungsraum"
];

const resolveGemaTypeMeta = (eventType) => {
    return GEMA_EVENT_TYPES.find((item) => item.value === eventType) || null;
};

const inferGemaTypeFromTitleCategory = (title, category) => {
    const normalize = (value) => String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const categoryNorm = normalize(category);
    const titleNorm = normalize(title);
    const haystack = `${titleNorm} ${categoryNorm}`.trim();

    if (!haystack) {
        return "";
    }

    const byCategory = [
        { test: /(konzert|live|chor|band)/, type: "TYPE_CONCERT" },
        { test: /(party|dj|club|disco|afterwork|rave)/, type: "TYPE_PARTY" },
        { test: /(sport|yoga|fitness|turnier)/, type: "TYPE_SPORT" },
        { test: /(strassenfest|strasenfest|outdoor|sommerfest)/, type: "TYPE_STREET" },
        { test: /(hintergrund|einlass|gastro|lounge)/, type: "TYPE_BG" }
    ];
    for (const rule of byCategory) {
        if (rule.test.test(categoryNorm)) {
            return rule.type;
        }
    }

    if (/(yoga|sport|fitness|turnier)/.test(haystack)) {
        return "TYPE_SPORT";
    }
    if (/(strassenfest|strasenfest|outdoor|sommerfest)/.test(haystack)) {
        return "TYPE_STREET";
    }
    if (/(party|dj|club|disco|rave)/.test(haystack)) {
        return "TYPE_PARTY";
    }
    if (/(konzert|live-act|live act|chor|band)/.test(haystack)) {
        return "TYPE_CONCERT";
    }
    if (/(hintergrund|einlass|gastro)/.test(haystack)) {
        return "TYPE_BG";
    }
    return "";
};

const RichTextEditor = ({ initialValue, onBlur, placeholder, readOnly = false, editorInstanceRef = null, editorId = "description" }) => {
    const editorRef = useRef(null);
    const initialEditorValue = replaceSeparatorMarkersForEditor(initialValue || "");

    return (
        <div className="space-y-2">
            <div className="rounded-lg border border-gray-300 overflow-hidden transition-all focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent">
                <Editor
                    id={editorId}
                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                    initialValue={initialEditorValue}
                    onInit={(_, editor) => {
                        editorRef.current = editor;
                        if (editorInstanceRef) {
                            editorInstanceRef.current = editor;
                        }
                    }}
                    disabled={readOnly}
                    onBlur={() => {
                        if (readOnly || !onBlur) {
                            return;
                        }
                        const editor = editorRef.current;
                        if (!editor) {
                            return;
                        }
                        const content = normalizeEditorMarkup(editor.getContent({ format: "raw" }));
                        const nextValue = isMeaningfulEditorHtml(content)
                            ? replaceSeparatorForStorage(content)
                            : "";
                        onBlur(nextValue);
                    }}
                    init={{
                        height: 280,
                        menubar: false,
                        branding: false,
                        statusbar: false,
                        promotion: false,
                        resize: true,
                        browser_spellcheck: true,
                        contextmenu: "undo redo | bold italic underline strikethrough | blocks fontfamily fontsize | link image media table",
                        convert_urls: false,
                        entity_encoding: "raw",
                        remove_trailing_brs: true,
                        placeholder,
                        paste_data_images: false,
                        paste_preprocess: (_plugin, args) => {
                            args.content = sanitizeDescriptionHtml(args.content);
                        },
                        plugins: [
                            "advlist", "anchor", "autolink", "autoresize", "charmap", "code", "codesample",
                            "emoticons", "fullscreen", "help", "image", "insertdatetime", "link",
                            "lists", "media", "preview", "searchreplace", "table", "visualblocks",
                            "visualchars", "wordcount", "quickbars"
                        ],
                        toolbar: "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | outdent indent | bullist numlist | blockquote | link image media table codesample charmap emoticons | searchreplace visualblocks code preview fullscreen | removeformat",
                        toolbar_mode: "wrap",
                        quickbars_selection_toolbar: "bold italic underline strikethrough | blocks | forecolor backcolor | quicklink blockquote",
                        quickbars_insert_toolbar: "quickimage quicktable",
                        min_height: 280,
                        max_height: 520,
                        autoresize_bottom_margin: 16,
                        forced_root_block: "p",
                        forced_root_block_attrs: {},
                        link_default_protocol: "https",
                        link_target_list: false,
                        image_advtab: true,
                        image_caption: true,
                        image_title: true,
                        table_advtab: true,
                        table_cell_advtab: true,
                        table_row_advtab: true,
                        table_appearance_options: true,
                        formats: {
                            bold: { inline: "strong", exact: true },
                            underline: { inline: "u", exact: true },
                            strikethrough: { inline: "s", exact: true }
                        },
                        style_formats: [
                            { title: "Absatz", block: "p" },
                            { title: "Ueberschrift 2", block: "h2" },
                            { title: "Ueberschrift 3", block: "h3" },
                            { title: "Zitat", block: "blockquote" },
                            { title: "Inline Code", inline: "code" }
                        ],
                        content_style: `
                            body {
                                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif;
                                font-size: 14px;
                                line-height: 1.6;
                                color: #111827;
                                padding: 12px 14px;
                            }
                            p, h2, h3, blockquote, pre { margin: 0 0 0.75rem; }
                            ul, ol { margin: 0 0 0.75rem; padding-left: 1.25rem; }
                            a { color: #2563eb; text-decoration: underline; }
                            hr.wp-block-separator {
                                border: 0;
                                border-top: 1px solid #d1d5db;
                                margin: 1rem 0;
                            }
                        `
                    }}
                />
            </div>
        </div>
    );
};

const CreateEvent = ({ user }) => {
    const navigate = useNavigate();
    const formRef = useRef(null);
    const noGemaConfirmBypassRef = useRef(false);
    const submitInFlightRef = useRef(false);
    const descriptionEditorRef = useRef(null);
    const descriptionDraftRef = useRef("");
    const prevStartDateRef = useRef(null);
    const [descriptionEditorVersion, setDescriptionEditorVersion] = useState(0);
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get("eventId");
    const duplicateFrom = searchParams.get("duplicateFrom");
    const isEditing = Boolean(eventId);
    const readOnlyParam = searchParams.get("readOnly") === "1";
    const returnTo = searchParams.get("returnTo");
    const returnPath = returnTo === "admin" ? "/admin" : returnTo === "my-events" ? "/my-events" : null;
    const userEmail = user?.email ? user.email.toLowerCase() : "";
    const isAdmin = user?.role === "admin";
    const fallbackReturnPath = isAdmin ? "/admin" : "/my-events";
    const effectiveReturnPath = returnPath || fallbackReturnPath;
    const [eventOwnerEmail, setEventOwnerEmail] = useState("");
    const [isReadOnly, setIsReadOnly] = useState(readOnlyParam);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [organizers, setOrganizers] = useState([]);
    const [organizersLoading, setOrganizersLoading] = useState(false);
    const [organizerEmail, setOrganizerEmail] = useState("");
    const [formData, setFormData] = useState({
        title: "",
        short_description: "",
        date: "",
        time: "",
        event_dates: [],
        category: "Konzert",
        description: "",
        image_url: "",
        gallery_image_urls: [],
        admission: "",
        door_time: "",
        end_time: "",
        admin_note: "",
        homepage_url: "",
        ticket_url: "",
        artist_names: [""],
        gema_relevant: false,
        gema_self_managed: false,
        gema_frei: false,
        gema_event_type: "",
        eintrittsgeld_netto: "",
        dj_costs_total: "",
        veranstaltungsraeume: [],
        besucher_anzahl: "",
        vinyl_only: false,
        ...GEMA_EXTRA_DEFAULTS,
        setlist_status: "Nicht notwendig",
        setlist_deadline: "",
        setlist_entries: [],
        is_kultur_for_all: false,
        sold_out: false,
        pricing_tiers: []
    });
    const [isMultiDay, setIsMultiDay] = useState(false);
    const [keytickMode, setKeytickMode] = useState("none");
    const defaultTicketDescription = "Dieses Ticket berechtigt dich für den Einlass zur Veranstaltung.";
    const [keytickData, setKeytickData] = useState({
        requiredAddress: false,
        eventPassword: "",
        maximalNumberOfAttendee: "",
        email: "",
        password: "",
        organisationId: "",
        tickets: [
            {
                title: "Standard-Ticket",
                description: defaultTicketDescription,
                maxNumberOfThisSellableTicket: "",
                peoplePerTicket: "1",
                graceTimeInSeconds: "0",
                priceNetto: "",
                taxPercent: "",
                taxNote: "",
                sellStartTime: "",
                sellEndTime: ""
            }
        ]
    });
    const [keytickOrganisations, setKeytickOrganisations] = useState([]);
    const [keytickOrgLoading, setKeytickOrgLoading] = useState(false);
    const [keytickExisting, setKeytickExisting] = useState(null);
    const [keytickBannerPreview, setKeytickBannerPreview] = useState("");
    const [keytickBannerBase64, setKeytickBannerBase64] = useState("");
    const lastAutoAdmissionPriceRef = useRef(null);
    const lastAutoGemaNettoRef = useRef(null);
    const lastAutoGemaTypeRef = useRef("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [customCategory, setCustomCategory] = useState("");
    const [isCustomCategory, setIsCustomCategory] = useState(false);
    const [eventStatus, setEventStatus] = useState("");
    const [rejectionMessage, setRejectionMessage] = useState("");
    const [syncingWordpress, setSyncingWordpress] = useState(false);
    const [recallingWordpress, setRecallingWordpress] = useState(false);
    const [wordpressUrl, setWordpressUrl] = useState("");
    const [hasWordpressConnection, setHasWordpressConnection] = useState(false);
    const [wordpressPublishError, setWordpressPublishError] = useState("");
    const [wordpressPublishFailedAt, setWordpressPublishFailedAt] = useState("");
    const [isImportedFromWp, setIsImportedFromWp] = useState(false);
    const adminEditLink = useMemo(() => {
        if (!isAdmin || !isEditing || !eventId || isImportedFromWp) {
            return "";
        }
        const params = new URLSearchParams({ eventId: String(eventId) });
        if (returnTo) {
            params.set("returnTo", returnTo);
        } else {
            params.set("returnTo", "admin");
        }
        return `/create?${params.toString()}`;
    }, [eventId, isAdmin, isEditing, isImportedFromWp, returnTo]);
    const [showNoGemaConfirmModal, setShowNoGemaConfirmModal] = useState(false);
    const [noGemaConfirmed, setNoGemaConfirmed] = useState(false);
    const [importEditConfirmed, setImportEditConfirmed] = useState(false);
    const [importEditPhrase, setImportEditPhrase] = useState("");
    const importBaselineRef = useRef(null);
    const gemaScheduleBaselineRef = useRef(null);
    const importEditPassphrase = "ICH WEISS WAS ICH TUE";
    const requiresImportConfirm = isAdmin && isEditing && isImportedFromWp;
    const importEditUnlocked = !requiresImportConfirm
        || (importEditConfirmed && importEditPhrase.trim().toUpperCase() === importEditPassphrase);

    const normalizeImportValue = (value) => {
        if (Array.isArray(value)) {
            return value.map((item) => normalizeImportValue(item));
        }
        if (value && typeof value === "object") {
            return Object.keys(value).sort().reduce((acc, key) => {
                acc[key] = normalizeImportValue(value[key]);
                return acc;
            }, {});
        }
        if (value === null || value === undefined) {
            return "";
        }
        if (typeof value === "string") {
            return value.trim();
        }
        return value;
    };

    const buildImportSnapshotFromEvent = (event) => {
        const artistNames = Array.isArray(event?.artist_names) && event.artist_names.length > 0
            ? event.artist_names
            : (event?.artist_name ? [event.artist_name] : [""]);
        return normalizeImportValue({
            title: event?.title || "",
            short_description: event?.short_description || "",
            date: event?.date || "",
            time: event?.time || "",
            event_dates: Array.isArray(event?.event_dates) ? event.event_dates : [],
            category: event?.category || "",
            category_is_new: Boolean(event?.category_is_new),
            custom_category: event?.category_is_new ? (event?.category || "") : "",
            description: event?.description || "",
            admission: event?.admission || "",
            door_time: event?.door_time || "",
            end_time: event?.end_time || "",
            admin_note: event?.admin_note || event?.additional_info || "",
            homepage_url: event?.homepage_url || "",
            ticket_url: event?.ticket_url || "",
            artist_names: artistNames,
            gema_relevant: Boolean(event?.gema_relevant),
            gema_self_managed: Boolean(event?.gema_self_managed),
            gema_frei: Boolean(event?.gema_frei),
            gema_event_type: event?.gema_event_type || "",
            eintrittsgeld_netto: event?.eintrittsgeld_netto ?? "",
            dj_costs_total: event?.dj_costs_total ?? "",
            veranstaltungsraeume: Array.isArray(event?.veranstaltungsraeume)
                ? event.veranstaltungsraeume
                : (event?.veranstaltungsraum ? [event.veranstaltungsraum] : []),
            besucher_anzahl: event?.besucher_anzahl ?? "",
            vinyl_only: Boolean(event?.vinyl_only),
            ...gemaExtrasFromEvent(event),
            setlist_status: event?.setlist_status || (event?.gema_relevant ? "Ausstehend" : "Nicht notwendig"),
            setlist_deadline: event?.setlist_deadline || "",
            setlist_entries: Array.isArray(event?.setlist_entries) ? event.setlist_entries : [],
            is_kultur_for_all: Boolean(event?.is_kultur_for_all),
            sold_out: Boolean(event?.sold_out),
            pricing_tiers: Array.isArray(event?.pricing_tiers) ? event.pricing_tiers : []
        });
    };

    const buildImportSnapshotFromForm = () => (
        normalizeImportValue({
            title: formData.title,
            short_description: formData.short_description,
            date: formData.date,
            time: formData.time,
            event_dates: Array.isArray(formData.event_dates) ? formData.event_dates : [],
            category: isCustomCategory ? customCategory : formData.category,
            category_is_new: Boolean(isCustomCategory),
            custom_category: isCustomCategory ? customCategory : "",
            description: formData.description,
            admission: formData.admission,
            door_time: formData.door_time,
            end_time: formData.end_time,
            admin_note: formData.admin_note,
            homepage_url: formData.homepage_url,
            ticket_url: formData.ticket_url,
            artist_names: Array.isArray(formData.artist_names) ? formData.artist_names : [],
            gema_relevant: Boolean(formData.gema_relevant),
            gema_self_managed: Boolean(formData.gema_self_managed),
            gema_frei: Boolean(formData.gema_frei),
            gema_event_type: formData.gema_event_type,
            eintrittsgeld_netto: formData.eintrittsgeld_netto,
            dj_costs_total: formData.dj_costs_total,
            veranstaltungsraeume: Array.isArray(formData.veranstaltungsraeume) ? formData.veranstaltungsraeume : [],
            besucher_anzahl: formData.besucher_anzahl,
            vinyl_only: Boolean(formData.vinyl_only),
            ...Object.fromEntries(Object.keys(GEMA_EXTRA_DEFAULTS).map((field) => [field, formData[field]])),
            setlist_status: formData.setlist_status,
            setlist_deadline: formData.setlist_deadline,
            setlist_entries: Array.isArray(formData.setlist_entries) ? formData.setlist_entries : [],
            is_kultur_for_all: Boolean(formData.is_kultur_for_all),
            sold_out: Boolean(formData.sold_out),
            pricing_tiers: Array.isArray(formData.pricing_tiers) ? formData.pricing_tiers : []
        })
    );
    
    const categories = useMemo(() => {
        if (availableCategories.length > 0) {
            return availableCategories.map((category) => decodeHtml(category.name));
        }
        return [
            "Konzert",
            "Party",
            "Theater",
            "Bunkerführung",
            "Ausstellung",
            "Workshop",
            "Lesung",
            "Film",
            "Sonstiges"
        ];
    }, [availableCategories]);

    const categoryExists = useMemo(() => {
        const trimmed = customCategory.trim().toLowerCase();
        return Boolean(trimmed && categories.some((category) => category.toLowerCase() === trimmed));
    }, [categories, customCategory]);

    useEffect(() => {
        if (isAdmin && !organizerEmail && !isEditing) {
            setOrganizerEmail("");
        }
    }, [isAdmin, organizerEmail, isEditing]);

    useEffect(() => {
        if (!isCustomCategory || !categoryExists) {
            return;
        }
        const normalized = customCategory.trim();
        if (!normalized) {
            return;
        }
        const normalizedLower = normalized.toLowerCase();
        const existingCategory = categories.find(
            (category) => category.toLowerCase() === normalizedLower
        ) || normalized;
        setIsCustomCategory(false);
        setCustomCategory("");
        setFormData((prev) => ({
            ...prev,
            category: existingCategory
        }));
    }, [categoryExists, customCategory, isCustomCategory]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setCategoryLoading(true);
                const data = await apiService.getEventCategories();
                if (Array.isArray(data)) {
                    setAvailableCategories(data);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setCategoryLoading(false);
            }
        };
        fetchCategories();
    }, []);

    const deriveSetlistDeadline = (dateValue) => {
        if (!dateValue) {
            return "";
        }
        const parsed = new Date(`${dateValue}T00:00:00`);
        if (Number.isNaN(parsed.getTime())) {
            return "";
        }
        parsed.setDate(parsed.getDate() + 14);
        return parsed.toISOString().slice(0, 10);
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "category") {
            setIsCustomCategory(false);
            setCustomCategory("");
        }
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === "date"
                ? {
                    setlist_deadline: deriveSetlistDeadline(value)
                }
                : {})
        }));
    };

    const syncDescriptionState = (value) => {
        setFormData(prev => ({
            ...prev,
            description: value
        }));
    };

    const handleDescriptionBlur = (value) => {
        descriptionDraftRef.current = value;
        syncDescriptionState(value);
    };

    const getCurrentDescriptionValue = () => {
        const editor = descriptionEditorRef.current;
        if (!editor) {
            return descriptionDraftRef.current || formData.description || "";
        }
        const content = normalizeEditorMarkup(editor.getContent({ format: "raw" }));
        if (!isMeaningfulEditorHtml(content)) {
            return "";
        }
        const nextValue = replaceSeparatorForStorage(content);
        descriptionDraftRef.current = nextValue;
        return nextValue;
    };

    const normalizeScheduleForPayload = (entries) => (
        (Array.isArray(entries) ? entries : [])
            .map((entry) => ({
                date: String(entry?.date || "").trim(),
                time: normalizeTimeInputValue(entry?.time),
                door_time: normalizeTimeInputValue(entry?.door_time),
                end_time: normalizeTimeInputValue(entry?.end_time)
            }))
            .filter((entry) => entry.date && entry.time)
            .map((entry) => ({
                ...entry,
                door_time: entry.door_time || null,
                end_time: entry.end_time || null
            }))
    );

    const updateScheduleEntry = (index, field, value) => {
        setFormData((prev) => {
            const nextDates = Array.isArray(prev.event_dates) ? [...prev.event_dates] : [];
            const current = nextDates[index] || { date: "", time: "", door_time: "", end_time: "" };
            nextDates[index] = { ...current, [field]: value };
            const first = nextDates[0] || {};
            return {
                ...prev,
                event_dates: nextDates,
                date: first.date || "",
                time: first.time || "",
                door_time: first.door_time || "",
                end_time: first.end_time || "",
                setlist_deadline: first.date ? deriveSetlistDeadline(first.date) : ""
            };
        });
    };

    const addScheduleEntry = () => {
        setFormData((prev) => ({
            ...prev,
            event_dates: [
                ...(Array.isArray(prev.event_dates) ? prev.event_dates : []),
                { date: "", time: "", door_time: "", end_time: "" }
            ]
        }));
    };

    const removeScheduleEntry = (index) => {
        setFormData((prev) => {
            const nextDates = (Array.isArray(prev.event_dates) ? prev.event_dates : [])
                .filter((_, itemIndex) => itemIndex !== index);
            const first = nextDates[0] || {};
            return {
                ...prev,
                event_dates: nextDates,
                date: first.date || "",
                time: first.time || "",
                door_time: first.door_time || "",
                end_time: first.end_time || "",
                setlist_deadline: first.date ? deriveSetlistDeadline(first.date) : ""
            };
        });
    };

    const handleMultiDayToggle = (checked) => {
        setIsMultiDay(checked);
        if (checked) {
            setFormData((prev) => {
                const existing = Array.isArray(prev.event_dates) ? prev.event_dates : [];
                const seeded = existing.length > 0
                    ? existing
                    : [{
                        date: prev.date || "",
                        time: prev.time || "",
                        door_time: prev.door_time || "",
                        end_time: prev.end_time || ""
                    }];
                return { ...prev, event_dates: seeded };
            });
        } else {
            setFormData((prev) => {
                const first = (Array.isArray(prev.event_dates) ? prev.event_dates : [])[0] || {};
                return {
                    ...prev,
                    date: first.date || prev.date || "",
                    time: first.time || prev.time || "",
                    door_time: first.door_time || "",
                    end_time: first.end_time || "",
                    setlist_deadline: (first.date || prev.date) ? deriveSetlistDeadline(first.date || prev.date) : "",
                    event_dates: []
                };
            });
        }
    };

    const handleWordpressSync = async () => {
        if (!eventId || !isAdmin) {
            return;
        }
        try {
            setSyncingWordpress(true);
            const response = await apiService.syncEventToWordpress(eventId);
            setWordpressUrl(response?.wordpress_url || wordpressUrl);
            setHasWordpressConnection(true);
            setWordpressPublishError("");
            setWordpressPublishFailedAt("");
            toast.success("WordPress-Sync abgeschlossen");
        } catch (error) {
            console.error("Error syncing event to WordPress:", error);
            const message = error.response?.data?.detail || "WordPress-Sync fehlgeschlagen";
            setWordpressPublishError(message);
            setWordpressPublishFailedAt(new Date().toISOString());
            toast.error(message);
        } finally {
            setSyncingWordpress(false);
        }
    };

    const handleWordpressPublish = async () => {
        if (!eventId || !isAdmin) {
            return;
        }
        try {
            setSyncingWordpress(true);
            const response = await apiService.publishEvent(eventId);
            setWordpressUrl(response?.wordpress_url || "");
            setHasWordpressConnection(true);
            setEventStatus("published");
            setWordpressPublishError("");
            setWordpressPublishFailedAt("");
            toast.success("Veranstaltung auf WordPress veröffentlicht");
        } catch (error) {
            console.error("Error publishing event to WordPress:", error);
            const message = error.response?.data?.detail || "Veröffentlichung auf WordPress fehlgeschlagen";
            setWordpressPublishError(message);
            setWordpressPublishFailedAt(new Date().toISOString());
            toast.error(message);
        } finally {
            setSyncingWordpress(false);
        }
    };

    const handleWordpressRecall = async () => {
        if (!eventId || !isAdmin) {
            return;
        }
        try {
            setRecallingWordpress(true);
            await apiService.recallEventFromWordpress(eventId);
            setEventStatus("pending");
            setRejectionMessage("");
            setWordpressUrl("");
            setHasWordpressConnection(false);
            setWordpressPublishError("");
            setWordpressPublishFailedAt("");
            toast.success("Veranstaltung aus WordPress zurückgeholt");
        } catch (error) {
            console.error("Error recalling event from WordPress:", error);
            const message = error.response?.data?.detail || "WordPress-Rückruf fehlgeschlagen";
            toast.error(message);
        } finally {
            setRecallingWordpress(false);
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => {
            if (name === "gema_relevant") {
                return {
                    ...prev,
                    gema_relevant: checked,
                    gema_self_managed: checked ? prev.gema_self_managed : false,
                    gema_frei: checked ? prev.gema_frei : false,
                    gema_event_type: checked ? prev.gema_event_type : "",
                    eintrittsgeld_netto: checked ? prev.eintrittsgeld_netto : "",
                    dj_costs_total: checked ? prev.dj_costs_total : "",
                    veranstaltungsraeume: checked ? prev.veranstaltungsraeume : [],
                    besucher_anzahl: checked ? prev.besucher_anzahl : "",
                    vinyl_only: checked ? prev.vinyl_only : false,
                    setlist_status: checked ? (prev.setlist_entries.length > 0 ? "Eingereicht" : "Ausstehend") : "Nicht notwendig",
                    setlist_deadline: checked ? prev.setlist_deadline : "",
                    setlist_entries: checked ? prev.setlist_entries : []
                };
            }
            // "Eigengema" und "nur GEMA-freie Musik" schließen sich aus.
            if (name === "gema_frei" && checked) {
                return { ...prev, gema_frei: true, gema_self_managed: false };
            }
            if (name === "gema_self_managed" && checked) {
                return { ...prev, gema_self_managed: true, gema_frei: false };
            }
            return {
                ...prev,
                [name]: checked
            };
        });
    };

    const toggleVeranstaltungsraum = (roomName) => {
        setFormData((prev) => {
            const current = Array.isArray(prev.veranstaltungsraeume) ? prev.veranstaltungsraeume : [];
            const exists = current.includes(roomName);
            return {
                ...prev,
                veranstaltungsraeume: exists
                    ? current.filter((item) => item !== roomName)
                    : [...current, roomName]
            };
        });
    };

    const handleKeytickChange = (e) => {
        const { name, value, type, checked } = e.target;
        setKeytickData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleKeytickTicketChange = (index, field, value) => {
        setKeytickData(prev => {
            const nextTickets = [...prev.tickets];
            const current = nextTickets[index] || {};
            nextTickets[index] = {
                ...current,
                [field]: value
            };
            return {
                ...prev,
                tickets: nextTickets
            };
        });
    };

    const addKeytickTicket = () => {
        setKeytickData(prev => ({
            ...prev,
            tickets: [
                ...prev.tickets,
                {
                    title: "",
                    description: "",
                    maxNumberOfThisSellableTicket: "",
                    peoplePerTicket: "1",
                    graceTimeInSeconds: "0",
                    priceNetto: "",
                    taxPercent: "",
                    taxNote: "",
                    sellStartTime: "",
                    sellEndTime: ""
                }
            ]
        }));
    };

    const handleKeytickBannerUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error("failed"));
                reader.readAsDataURL(file);
            });
            const image = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error("failed"));
                img.src = dataUrl;
            });
            const targetWidth = 2000;
            const targetHeight = 1200;
            const canvas = document.createElement("canvas");
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                throw new Error("no_canvas");
            }
            const sourceRatio = image.width / image.height;
            const targetRatio = targetWidth / targetHeight;
            let sx = 0;
            let sy = 0;
            let sWidth = image.width;
            let sHeight = image.height;
            if (sourceRatio > targetRatio) {
                sWidth = Math.round(image.height * targetRatio);
                sx = Math.round((image.width - sWidth) / 2);
            } else if (sourceRatio < targetRatio) {
                sHeight = Math.round(image.width / targetRatio);
                sy = Math.round((image.height - sHeight) / 2);
            }
            ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);
            const previewUrl = canvas.toDataURL("image/jpeg", 0.9);
            const base64 = previewUrl.split(",", 2)[1] || "";
            setKeytickBannerPreview(previewUrl);
            setKeytickBannerBase64(base64);
        } catch (error) {
            console.error("KeyTick banner processing failed:", error);
            toast.error("KeyTick Banner konnte nicht verarbeitet werden.");
        }
    };

    const removeKeytickTicket = (index) => {
        if (index === 0) {
            return;
        }
        setKeytickData(prev => ({
            ...prev,
            tickets: prev.tickets.filter((_, idx) => idx !== index)
        }));
    };

    const loadKeytickOrganisations = async () => {
        if (!keytickData.email || !keytickData.password) {
            toast.error("Bitte E-Mail und Passwort für KeyTick angeben.");
            return;
        }
        try {
            setKeytickOrgLoading(true);
            const response = await apiService.getKeytickOrganisations({
                mode: "mode_b",
                email: keytickData.email,
                password: keytickData.password
            });
            const organisations = Array.isArray(response?.organisations) ? response.organisations : [];
            setKeytickOrganisations(organisations);
            if (organisations.length === 1 && !keytickData.organisationId) {
                setKeytickData(prev => ({
                    ...prev,
                    organisationId: organisations[0].organisationId
                }));
            }
        } catch (error) {
            console.error("KeyTick Organisationen konnten nicht geladen werden:", error);
            const message = error.response?.data?.detail || "KeyTick Organisationen konnten nicht geladen werden";
            toast.error(message);
        } finally {
            setKeytickOrgLoading(false);
        }
    };

    const handlePricingTierChange = (index, field, value) => {
        setFormData(prev => {
            const nextTiers = [...prev.pricing_tiers];
            nextTiers[index] = {
                ...nextTiers[index],
                [field]: value
            };
            return {
                ...prev,
                pricing_tiers: nextTiers
            };
        });
    };

    const addPricingTier = () => {
        setFormData(prev => ({
            ...prev,
            pricing_tiers: [
                ...prev.pricing_tiers,
                { label: "", price: "" }
            ]
        }));
    };

    const removePricingTier = (index) => {
        setFormData(prev => ({
            ...prev,
            pricing_tiers: prev.pricing_tiers.filter((_, tierIndex) => tierIndex !== index)
        }));
    };

    const handleRemoveGalleryImage = (index) => {
        setFormData(prev => ({
            ...prev,
            gallery_image_urls: prev.gallery_image_urls.filter((_, imgIndex) => imgIndex !== index)
        }));
    };

    const addSetlistEntry = () => {
        setFormData((prev) => ({
            ...prev,
            setlist_entries: [
                ...(Array.isArray(prev.setlist_entries) ? prev.setlist_entries : []),
                {
                    titel: "",
                    komponist: "",
                    interpret_original: "",
                    dauer_sekunden: "",
                    bearbeiter: "",
                    verlag: ""
                }
            ]
        }));
    };

    const updateSetlistEntry = (index, field, value) => {
        setFormData((prev) => {
            const nextEntries = Array.isArray(prev.setlist_entries) ? [...prev.setlist_entries] : [];
            nextEntries[index] = {
                ...(nextEntries[index] || {}),
                [field]: value
            };
            return {
                ...prev,
                setlist_entries: nextEntries,
                setlist_status: nextEntries.some((entry) => (entry.titel || "").trim() && (entry.komponist || "").trim())
                    ? "Eingereicht"
                    : "Ausstehend"
            };
        });
    };

    const removeSetlistEntry = (index) => {
        setFormData((prev) => {
            const nextEntries = (Array.isArray(prev.setlist_entries) ? prev.setlist_entries : []).filter((_, entryIndex) => entryIndex !== index);
            return {
                ...prev,
                setlist_entries: nextEntries,
                setlist_status: nextEntries.length > 0 ? "Eingereicht" : "Ausstehend"
            };
        });
    };

    const addArtistName = () => {
        setFormData((prev) => ({
            ...prev,
            artist_names: [...(Array.isArray(prev.artist_names) ? prev.artist_names : []), ""]
        }));
    };

    const updateArtistName = (index, value) => {
        setFormData((prev) => {
            const next = Array.isArray(prev.artist_names) ? [...prev.artist_names] : [""];
            next[index] = value;
            return { ...prev, artist_names: next };
        });
    };

    const removeArtistName = (index) => {
        setFormData((prev) => {
            const next = (Array.isArray(prev.artist_names) ? prev.artist_names : []).filter((_, i) => i !== index);
            return { ...prev, artist_names: next.length > 0 ? next : [""] };
        });
    };
    
    useEffect(() => {
        const sourceEventId = eventId || duplicateFrom;
        const isDuplicateMode = !eventId && Boolean(duplicateFrom);
        if (!sourceEventId) {
            setEventOwnerEmail("");
            setKeytickMode("none");
            setKeytickExisting(null);
            setIsMultiDay(false);
            setEventStatus("");
            setRejectionMessage("");
            setWordpressUrl("");
            setHasWordpressConnection(false);
            setWordpressPublishError("");
            setWordpressPublishFailedAt("");
            descriptionEditorRef.current = null;
            descriptionDraftRef.current = "";
            setDescriptionEditorVersion((prev) => prev + 1);
            setFormData((prev) => ({ ...prev, event_dates: [] }));
            return;
        }
        const loadEvent = async () => {
            try {
                setInitialLoading(true);
                const event = await apiService.getEvent(sourceEventId);
                setKeytickMode("none");
                if (isDuplicateMode) {
                    setEventOwnerEmail("");
                    setOrganizerEmail(userEmail || "");
                    setEventStatus("");
                    setRejectionMessage("");
                    setWordpressUrl("");
                    setHasWordpressConnection(false);
                    setWordpressPublishError("");
                    setWordpressPublishFailedAt("");
                    setIsImportedFromWp(false);
                    setImportEditConfirmed(false);
                    setImportEditPhrase("");
                    importBaselineRef.current = null;
                    gemaScheduleBaselineRef.current = null;
                } else {
                    gemaScheduleBaselineRef.current = event.gema_registered
                        ? gemaScheduleSignature(event)
                        : null;
                    setEventOwnerEmail(event.created_by_email?.toLowerCase() || "");
                    setOrganizerEmail(event.created_by_email?.toLowerCase() || "");
                    setEventStatus(event.status || "");
                    setRejectionMessage(event.rejection_message || "");
                    setWordpressUrl(event.wordpress_url || "");
                    setHasWordpressConnection(Boolean(
                        event.wordpress_url || event.wordpress_id || event.wordpress_block_id || (event.wordpress_event_refs || []).length
                    ));
                    setWordpressPublishError(event.wordpress_publish_error || "");
                    setWordpressPublishFailedAt(event.wordpress_publish_failed_at || "");
                    setIsImportedFromWp(Boolean(event.imported_from_wp));
                    setImportEditConfirmed(false);
                    setImportEditPhrase("");
                    importBaselineRef.current = {
                        snapshot: buildImportSnapshotFromEvent(event),
                        image_url: (event.cover_image_url || event.image_url || "").trim(),
                        gallery_image_urls: Array.isArray(event.gallery_image_urls) ? event.gallery_image_urls : []
                    };
                }
                const eventDates = Array.isArray(event.event_dates) ? event.event_dates : [];
                const normalizedEventDates = eventDates.map((entry) => ({
                    ...entry,
                    time: normalizeTimeInputValue(entry?.time),
                    door_time: normalizeTimeInputValue(entry?.door_time),
                    end_time: normalizeTimeInputValue(entry?.end_time)
                }));
                setIsMultiDay(isDuplicateMode ? false : eventDates.length > 0);
                const imageUrl = event.cover_image_url || event.image_url || "";
                const primaryDate = normalizedEventDates[0] || {};
                setFormData({
                    title: isDuplicateMode ? `${event.title || ""} (Kopie)` : (event.title || ""),
                    short_description: event.short_description || "",
                    date: isDuplicateMode ? "" : (primaryDate.date || event.date || ""),
                    time: isDuplicateMode ? "" : (primaryDate.time || normalizeTimeInputValue(event.time) || ""),
                    event_dates: isDuplicateMode ? [] : normalizedEventDates,
                    category: event.category || "Konzert",
                    category_is_new: Boolean(event.category_is_new),
                    description: event.description || "",
                    image_url: imageUrl,
                    gallery_image_urls: Array.isArray(event.gallery_image_urls) ? event.gallery_image_urls : [],
                    admission: event.admission || "",
                    door_time: isDuplicateMode ? "" : (primaryDate.door_time || normalizeTimeInputValue(event.door_time) || ""),
                    end_time: isDuplicateMode ? "" : normalizeDateTimeLocalInputValue(primaryDate.end_time || event.end_time, primaryDate.date || event.date || ""),
                    admin_note: event.admin_note || event.additional_info || "",
                    homepage_url: event.homepage_url || "",
                    ticket_url: event.ticket_url || "",
                    artist_names: Array.isArray(event.artist_names) && event.artist_names.length > 0
                        ? event.artist_names
                        : (event.artist_name ? [event.artist_name] : [""]),
                    gema_relevant: Boolean(event.gema_relevant),
                    gema_self_managed: Boolean(event.gema_self_managed),
                    gema_frei: Boolean(event.gema_frei),
                    gema_event_type: event.gema_event_type || "",
                    eintrittsgeld_netto: normalizeNumericInputValue(event.eintrittsgeld_netto),
                    dj_costs_total: normalizeNumericInputValue(event.dj_costs_total),
                    veranstaltungsraeume: Array.isArray(event.veranstaltungsraeume)
                        ? event.veranstaltungsraeume
                        : (event.veranstaltungsraum ? [event.veranstaltungsraum] : []),
                    besucher_anzahl: event.besucher_anzahl ?? "",
                    vinyl_only: Boolean(event.vinyl_only),
                    ...gemaExtrasFromEvent(event),
                    setlist_status: isDuplicateMode
                        ? (event.gema_relevant ? "Ausstehend" : "Nicht notwendig")
                        : (event.setlist_status || (event.gema_relevant ? "Ausstehend" : "Nicht notwendig")),
                    setlist_deadline: isDuplicateMode ? "" : (event.setlist_deadline || ""),
                    setlist_entries: isDuplicateMode ? [] : (Array.isArray(event.setlist_entries) ? event.setlist_entries : []),
                    is_kultur_for_all: Boolean(event.is_kultur_for_all),
                    sold_out: isDuplicateMode ? false : Boolean(event.sold_out),
                    pricing_tiers: Array.isArray(event.pricing_tiers) ? event.pricing_tiers : []
                });
                descriptionEditorRef.current = null;
                descriptionDraftRef.current = event.description || "";
                setDescriptionEditorVersion((prev) => prev + 1);
                if (!isDuplicateMode && (event.keytick_event_id || event.keytick_event_url)) {
                    setKeytickExisting({
                        eventId: event.keytick_event_id || "",
                        eventUrl: event.keytick_event_url || "",
                        eventUrlConfigured: Boolean(event.keytick_event_url_configured)
                    });
                } else if (isDuplicateMode) {
                    setKeytickExisting(null);
                }
                if (event.category_is_new) {
                    setIsCustomCategory(true);
                    setCustomCategory(event.category || "");
                }
            } catch (error) {
                console.error("Error loading event:", error);
                toast.error("Veranstaltung konnte nicht geladen werden");
                navigate("/");
            } finally {
                setInitialLoading(false);
            }
        };
        loadEvent();
    }, [eventId, duplicateFrom, navigate, userEmail]);

    useEffect(() => {
        const loadOrganizers = async () => {
            if (!isAdmin) {
                return;
            }
            try {
                setOrganizersLoading(true);
                const data = await apiService.getUsers();
                const options = (data || [])
                    .filter((item) => item.role === "organizer" || item.role === "admin")
                    .sort((a, b) => {
                        const nameA = (a.name || a.email || "").toLowerCase();
                        const nameB = (b.name || b.email || "").toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                setOrganizers(options);
            } catch (error) {
                console.error("Error loading organizers:", error);
                toast.error("Veranstalter konnten nicht geladen werden");
            } finally {
                setOrganizersLoading(false);
            }
        };
        loadOrganizers();
    }, [isAdmin]);

    useEffect(() => {
        if (!isEditing) {
            setIsReadOnly(readOnlyParam);
            return;
        }
        if (!eventOwnerEmail) {
            setIsReadOnly(readOnlyParam);
            return;
        }
        const canEditEvent = isAdmin || (userEmail && eventOwnerEmail === userEmail);
        setIsReadOnly(readOnlyParam || !canEditEvent);
    }, [eventOwnerEmail, isAdmin, isEditing, readOnlyParam, userEmail]);

    useEffect(() => {
        if (!eventId && categories.length > 0 && !formData.category) {
            setFormData(prev => ({
                ...prev,
                category: categories[0]
            }));
        }
    }, [categories, eventId, formData.category]);

    useEffect(() => {
        const oldDate = prevStartDateRef.current;
        prevStartDateRef.current = formData.date || null;
        // Only sync end_time date when the start date actually changes from a known previous date.
        // Skipping on initial load (oldDate === null) prevents corrupting a next-day end_time
        // that was correctly stored as e.g. "2024-01-16T02:00" when the start is "2024-01-15".
        if (!formData.date || oldDate === null || oldDate === formData.date) {
            return;
        }
        setFormData((prev) => {
            const currentEndTime = String(prev.end_time || "").trim();
            if (!currentEndTime || !currentEndTime.includes("T")) {
                return prev;
            }
            const [currentEndDate, timeWithSeconds] = currentEndTime.split("T");
            const currentTimePart = timeWithSeconds?.slice(0, 5);
            if (!currentTimePart || !/^\d{2}:\d{2}$/.test(currentTimePart)) {
                return prev;
            }
            // Preserve the day offset: if end was 1 day after old start, keep it 1 day after new start.
            const oldDateObj = new Date(`${oldDate}T00:00:00`);
            const endDateObj = new Date(`${currentEndDate}T00:00:00`);
            const newDateObj = new Date(`${prev.date}T00:00:00`);
            if (Number.isNaN(oldDateObj.getTime()) || Number.isNaN(endDateObj.getTime()) || Number.isNaN(newDateObj.getTime())) {
                return prev;
            }
            const offsetDays = Math.round((endDateObj - oldDateObj) / 86400000);
            newDateObj.setDate(newDateObj.getDate() + offsetDays);
            const yyyy = newDateObj.getFullYear();
            const mm = String(newDateObj.getMonth() + 1).padStart(2, "0");
            const dd = String(newDateObj.getDate()).padStart(2, "0");
            const nextEndTime = `${yyyy}-${mm}-${dd}T${currentTimePart}`;
            if (nextEndTime === currentEndTime) {
                return prev;
            }
            return { ...prev, end_time: nextEndTime };
        });
    }, [formData.date]);

    useEffect(() => {
        if (!formData.gema_relevant || !formData.date) {
            return;
        }
        const parsed = new Date(`${formData.date}T00:00:00`);
        if (Number.isNaN(parsed.getTime())) {
            return;
        }
        parsed.setDate(parsed.getDate() + 14);
        const computedDeadline = parsed.toISOString().slice(0, 10);
        if (computedDeadline === formData.setlist_deadline) {
            return;
        }
        setFormData((prev) => ({
            ...prev,
            setlist_deadline: computedDeadline
        }));
    }, [formData.gema_relevant, formData.date, formData.setlist_deadline]);

    const inferredGemaTypeFromCategory = useMemo(
        () => inferGemaTypeFromTitleCategory(formData.title, formData.category),
        [formData.title, formData.category]
    );

    useEffect(() => {
        if (!formData.gema_relevant) {
            return;
        }
        const inferredType = inferredGemaTypeFromCategory;
        if (!inferredType) {
            return;
        }
        setFormData((prev) => {
            const currentType = prev.gema_event_type || "";
            const canAutoReplace = !currentType || currentType === lastAutoGemaTypeRef.current;
            if (!canAutoReplace || currentType === inferredType) {
                return prev;
            }
            lastAutoGemaTypeRef.current = inferredType;
            return {
                ...prev,
                gema_event_type: inferredType
            };
        });
    }, [formData.gema_relevant, inferredGemaTypeFromCategory]);

    useEffect(() => {
        if (!formData.gema_relevant) {
            return;
        }
        setFormData((prev) => {
            const typeMeta = resolveGemaTypeMeta(prev.gema_event_type);
            const required = Boolean(typeMeta?.setlistRequired);
            if (required) {
                if (prev.setlist_status === "Nicht notwendig") {
                    return {
                        ...prev,
                        setlist_status: prev.setlist_entries.length > 0 ? "Eingereicht" : "Ausstehend"
                    };
                }
                return prev;
            }
            if (prev.setlist_status === "Nicht notwendig" && prev.setlist_entries.length === 0) {
                return prev;
            }
            return {
                ...prev,
                setlist_status: "Nicht notwendig",
                setlist_entries: []
            };
        });
    }, [formData.gema_relevant, formData.gema_event_type]);

    const gemaTypeMeta = useMemo(
        () => resolveGemaTypeMeta(formData.gema_event_type),
        [formData.gema_event_type]
    );
    const gemaTarifVorschlag = gemaTypeMeta?.tariff || "";
    const isSetlistRequired = Boolean(gemaTypeMeta?.setlistRequired);

    const gemaEintrittNettoWert = useMemo(() => {
        if (!formData.eintrittsgeld_netto && formData.eintrittsgeld_netto !== 0) {
            return null;
        }
        const normalized = String(formData.eintrittsgeld_netto).replace(",", ".");
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }, [formData.eintrittsgeld_netto]);

    const parseAdmissionPrice = (value) => {
        if (!value) {
            return null;
        }
        const match = String(value).match(/[\d.,]+/g);
        if (!match) {
            return null;
        }
        const token = match[0];
        const hasComma = token.includes(",");
        const hasDot = token.includes(".");
        let normalized = token;
        if (hasComma && hasDot) {
            const lastComma = token.lastIndexOf(",");
            const lastDot = token.lastIndexOf(".");
            const decimalIndex = Math.max(lastComma, lastDot);
            normalized = token
                .split("")
                .map((char, idx) => {
                    if ((char === "," || char === ".") && idx !== decimalIndex) {
                        return "";
                    }
                    return char === "," ? "." : char;
                })
                .join("");
        } else if (hasComma) {
            const parts = token.split(",");
            if (parts.length === 2 && parts[1].length > 0 && parts[1].length <= 2) {
                normalized = `${parts[0].replace(/\./g, "")}.${parts[1]}`;
            } else {
                normalized = token.replace(/,/g, "");
            }
        } else if (hasDot) {
            const parts = token.split(".");
            if (parts.length === 2 && parts[1].length > 0 && parts[1].length <= 2) {
                normalized = `${parts[0].replace(/,/g, "")}.${parts[1]}`;
            } else {
                normalized = token.replace(/\./g, "");
            }
        }
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    };

    useEffect(() => {
        if (!formData.gema_relevant) {
            return;
        }
        const admissionText = String(formData.admission || "");
        if (!admissionText.includes("€")) {
            return;
        }
        const gross = parseAdmissionPrice(admissionText);
        if (gross === null) {
            return;
        }
        const netto = Math.round((gross / 1.19) * 100) / 100;
        const currentRaw = String(formData.eintrittsgeld_netto ?? "").trim();
        const currentNumeric = currentRaw === "" ? null : Number(currentRaw.replace(",", "."));
        const lastAutoNumeric = lastAutoGemaNettoRef.current;
        const canAutofill = (
            currentRaw === ""
            || (Number.isFinite(currentNumeric) && lastAutoNumeric !== null && currentNumeric === lastAutoNumeric)
        );
        if (!canAutofill) {
            return;
        }
        if (currentNumeric !== null && Number.isFinite(currentNumeric) && currentNumeric === netto) {
            lastAutoGemaNettoRef.current = netto;
            return;
        }
        lastAutoGemaNettoRef.current = netto;
        setFormData((prev) => ({
            ...prev,
            eintrittsgeld_netto: netto.toFixed(2)
        }));
    }, [formData.admission, formData.eintrittsgeld_netto, formData.gema_relevant]);

    const normalizeDateTimeLocal = (value, dateFallback) => {
        if (!value) {
            return "";
        }
        let normalized = String(value).trim();
        if (!normalized) {
            return "";
        }
        if (/^\d{1,2}:\d{2}$/.test(normalized)) {
            if (!dateFallback) {
                return "";
            }
            return `${dateFallback}T${normalized.padStart(5, "0")}`;
        }
        if (/^\d{4}-\d{2}-\d{2} /.test(normalized)) {
            normalized = normalized.replace(" ", "T");
        }
        normalized = normalized.replace(/([+-]\d{2}:\d{2}|Z)$/i, "");
        const isValid = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(normalized);
        return isValid ? normalized : "";
    };

    useEffect(() => {
        if (keytickMode === "none" || formData.pricing_tiers.length === 0) {
            return;
        }
        setKeytickData(prev => {
            const baseTicket = prev.tickets[0] || {};
            const nextTickets = [...prev.tickets];
            formData.pricing_tiers.forEach((tier, index) => {
                const ticketIndex = index + 1;
                const current = nextTickets[ticketIndex] || {};
                const parsedPrice = parseAdmissionPrice(tier.price);
                nextTickets[ticketIndex] = {
                    ...current,
                    title: current.title?.trim() ? current.title : (tier.label?.trim() || `Staffelpreis ${index + 1}`),
                    description: current.description?.trim()
                        ? current.description
                        : (baseTicket.description || defaultTicketDescription),
                    priceNetto: String(current.priceNetto || "").trim()
                        ? current.priceNetto
                        : (parsedPrice !== null ? String(parsedPrice) : current.priceNetto),
                    maxNumberOfThisSellableTicket: current.maxNumberOfThisSellableTicket || baseTicket.maxNumberOfThisSellableTicket,
                    peoplePerTicket: current.peoplePerTicket || baseTicket.peoplePerTicket || "1",
                    graceTimeInSeconds: current.graceTimeInSeconds || baseTicket.graceTimeInSeconds || "0",
                    taxPercent: current.taxPercent || baseTicket.taxPercent,
                    taxNote: current.taxNote || baseTicket.taxNote,
                    sellStartTime: current.sellStartTime || normalizeDateTimeLocal(baseTicket.sellStartTime, formData.date),
                    sellEndTime: current.sellEndTime || normalizeDateTimeLocal(baseTicket.sellEndTime, formData.date)
                };
            });
            return { ...prev, tickets: nextTickets };
        });
    }, [formData.pricing_tiers, keytickMode]);

    useEffect(() => {
        const admissionPrice = parseAdmissionPrice(formData.admission);
        if (admissionPrice === null || !keytickData.tickets[0]) {
            return;
        }
        const currentPrice = String(keytickData.tickets[0].priceNetto || "").trim();
        const lastAutoPrice = lastAutoAdmissionPriceRef.current;
        if (currentPrice && (lastAutoPrice === null || currentPrice !== String(lastAutoPrice))) {
            return;
        }
        setKeytickData(prev => {
            if (!prev.tickets[0]) {
                return prev;
            }
            const nextCurrent = String(prev.tickets[0].priceNetto || "").trim();
            if (nextCurrent && (lastAutoPrice === null || nextCurrent !== String(lastAutoPrice))) {
                return prev;
            }
            if (nextCurrent === String(admissionPrice)) {
                return prev;
            }
            const nextTickets = [...prev.tickets];
            nextTickets[0] = {
                ...nextTickets[0],
                priceNetto: String(admissionPrice)
            };
            lastAutoAdmissionPriceRef.current = admissionPrice;
            return {
                ...prev,
                tickets: nextTickets
            };
        });
    }, [formData.admission, keytickData.tickets]);
    
    const submitImageOnlyUpdate = async () => {
        const normalizedImageUrl = (formData.image_url || "").trim();
        const normalizedGallery = (Array.isArray(formData.gallery_image_urls)
            ? formData.gallery_image_urls
            : []
        ).filter(Boolean).map((url) => String(url).trim());
        if (!normalizedImageUrl && normalizedGallery.length === 0) {
            toast.error("Bitte mindestens ein Bild auswählen.");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                image_url: normalizedImageUrl,
                cover_image_url: normalizedImageUrl || null,
                gallery_image_urls: normalizedGallery
            };
            await apiService.updateEventImages(eventId, payload);
            toast.success("Bilder gespeichert!");
        } catch (error) {
            console.error("Error updating images:", error);
            const message = error.response?.data?.detail || "Fehler beim Speichern der Bilder";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitInFlightRef.current) {
            return;
        }

        const baseline = importBaselineRef.current;
        const normalizedImageUrl = (formData.image_url || "").trim();
        const normalizedGallery = (Array.isArray(formData.gallery_image_urls)
            ? formData.gallery_image_urls
            : []
        ).filter(Boolean).map((url) => String(url).trim());
        const schedulePayload = isMultiDay ? normalizeScheduleForPayload(formData.event_dates) : [];
        const hasIncompleteSchedule = isMultiDay && (Array.isArray(formData.event_dates) ? formData.event_dates : [])
            .some((entry) => {
                const hasAnyValue = Boolean(
                    String(entry?.date || "").trim()
                    || String(entry?.time || "").trim()
                    || String(entry?.door_time || "").trim()
                    || String(entry?.end_time || "").trim()
                );
                return hasAnyValue && (
                    !String(entry?.date || "").trim()
                    || !String(entry?.time || "").trim()
                );
            });
        const imageOnlyUpdate = Boolean(
            requiresImportConfirm
            && baseline
            && !importEditUnlocked
            && (
                normalizedImageUrl !== (baseline.image_url || "")
                || JSON.stringify(normalizedGallery) !== JSON.stringify(baseline.gallery_image_urls || [])
            )
            && JSON.stringify(buildImportSnapshotFromForm()) === JSON.stringify(baseline.snapshot)
        );

        if (isReadOnly) {
            toast.error("Diese Veranstaltung ist schreibgeschützt.");
            return;
        }
        if (!isEditing && !formData.gema_relevant && !noGemaConfirmed && !noGemaConfirmBypassRef.current) {
            setShowNoGemaConfirmModal(true);
            return;
        }
        noGemaConfirmBypassRef.current = false;
        if (isMultiDay) {
            if (hasIncompleteSchedule) {
                toast.error("Bitte alle Termine mit Datum und Startzeit ausfüllen.");
                return;
            }
            if (schedulePayload.length === 0) {
                toast.error("Bitte mindestens einen Termin hinzufügen.");
                return;
            }
        }
        if (requiresImportConfirm && !importEditUnlocked && !imageOnlyUpdate) {
            toast.error("WordPress-Import: Bitte bestätige die Warnung, um zu speichern.");
            return;
        }
        if (imageOnlyUpdate && !normalizedImageUrl && normalizedGallery.length === 0) {
            toast.error("Bitte mindestens ein Bild auswählen.");
            return;
        }
        if (imageOnlyUpdate) {
            await submitImageOnlyUpdate();
            return;
        }
        const currentDescriptionValue = getCurrentDescriptionValue();
        if (!formData.title || !formData.date || !formData.time || !formData.admission) {
            toast.error("Bitte füllen Sie alle Pflichtfelder aus");
            return;
        }
        if (!formData.short_description.trim()) {
            toast.error("Bitte fügen Sie eine Kurzbeschreibung hinzu");
            return;
        }
        const trimmedTitle = (formData.title || "").trim();
        if (trimmedTitle.length > 80) {
            toast.error("Titel darf maximal 80 Zeichen haben.");
            return;
        }
        const trimmedAdmission = (formData.admission || "").trim();
        if (trimmedAdmission.length > 40) {
            toast.error("Eintritt darf maximal 40 Zeichen haben.");
            return;
        }
        const descriptionText = stripHtml(currentDescriptionValue || "");
        if (descriptionText.length > 3500) {
            toast.error("Beschreibung darf maximal 3500 Zeichen haben.");
            return;
        }
        const parseNumber = (value) => {
            if (value === "" || value === null || value === undefined) {
                return null;
            }
            const normalized = String(value).replace(",", ".");
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : null;
        };
        const parseInteger = (value) => {
            const parsed = parseNumber(value);
            return parsed === null ? null : Math.trunc(parsed);
        };
        if (formData.gema_relevant && !formData.gema_self_managed && !formData.gema_frei) {
            const eintrittsgeldNetto = parseNumber(formData.eintrittsgeld_netto);
            const djCostsTotal = parseNumber(formData.dj_costs_total);
            const besucherAnzahl = parseInteger(formData.besucher_anzahl);
            if (!formData.gema_event_type) {
                toast.error("Bitte auswählen, was stattfindet.");
                return;
            }
            if (
                eintrittsgeldNetto === null
                || djCostsTotal === null
                || !Array.isArray(formData.veranstaltungsraeume)
                || formData.veranstaltungsraeume.length === 0
                || besucherAnzahl === null
            ) {
                toast.error("Bitte alle GEMA-Stammdaten ausfüllen.");
                return;
            }
            if (gemaTarifVorschlag === "M-SP" && gemaEintrittNettoWert !== null && gemaEintrittNettoWert > 10) {
                toast.warning("Hohe GEMA-Kosten möglich! Bitte Eintrittspreis prüfen (ggf. Kursgebühr splitten).");
            }
        }
        const scheduleForValidation = isMultiDay
            ? schedulePayload
            : [{ date: formData.date, time: formData.time, end_time: formData.end_time }];
        for (const entry of scheduleForValidation) {
            const startDateTime = buildLocalDateTime(entry.date, entry.time);
            const endDateTime = buildLocalDateTime(entry.date, entry.end_time);
            const endRaw = String(entry.end_time || "").trim();
            const endHasDate = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(endRaw);
            if (endRaw && endHasDate && startDateTime && endDateTime && endDateTime < startDateTime) {
                toast.error("Endzeit darf nicht vor Startzeit liegen.");
                return;
            }
        }
        if (!isEditing) {
            const startTimes = scheduleForValidation
                .map((entry) => buildLocalDateTime(entry.date, entry.time))
                .filter(Boolean);
            const earliest = startTimes.length > 0
                ? startTimes.reduce((min, current) => (current < min ? current : min))
                : null;
            if (earliest && earliest.getTime() <= Date.now()) {
                toast.error("Startzeit muss in der Zukunft liegen.");
                return;
            }
        }

        const keytickEnabled = !isEditing && keytickMode !== "none" && !keytickExisting?.eventId;
        if (keytickMode !== "none" && keytickExisting?.eventId) {
            toast.error("KeyTick wurde bereits für diese Veranstaltung erstellt.");
            return;
        }
        if (isEditing && keytickMode !== "none") {
            toast.error("KeyTick kann nur beim Erstellen einer Veranstaltung angelegt werden.");
            return;
        }

        const sanitizedPricingTiers = formData.pricing_tiers
            .map((tier) => ({
                label: tier.label.trim(),
                price: tier.price.trim()
            }))
            .filter((tier) => tier.label || tier.price);
        if (keytickEnabled) {
            if (!formData.end_time) {
                toast.error("Bitte geben Sie eine Endzeit für KeyTick an.");
                return;
            }
            if (!formData.image_url) {
                toast.error("Bitte laden Sie ein Titelbild für KeyTick hoch.");
                return;
            }
            const maximalNumberOfAttendee = parseInteger(keytickData.maximalNumberOfAttendee);
            if (maximalNumberOfAttendee === null || maximalNumberOfAttendee <= 0) {
                toast.error("Bitte eine gueltige maximale Teilnehmerzahl fuer KeyTick angeben.");
                return;
            }
            if (keytickMode === "mode_b") {
                if (!keytickData.email.trim() || !keytickData.password) {
                    toast.error("Bitte KeyTick Login-Daten angeben.");
                    return;
                }
                if (!keytickData.organisationId) {
                    toast.error("Bitte eine KeyTick Organisation auswählen.");
                    return;
                }
            }
            const admissionPrice = parseAdmissionPrice(formData.admission);
            if (sanitizedPricingTiers.some((tier) => parseAdmissionPrice(tier.price) === null)) {
                toast.error("Staffelpreis enthält keinen gültigen Preis.");
                return;
            }
            const requiredTicketsValid = keytickData.tickets.every((ticket, index) => {
                const titleValue = index === 0 ? "Standard-Ticket" : ticket.title;
                const descriptionValue = ticket.description || defaultTicketDescription;
                const rawPrice = index === 0 && admissionPrice !== null && !String(ticket.priceNetto || "").trim()
                    ? String(admissionPrice)
                    : ticket.priceNetto;
                const requiredTicketFields = [
                    titleValue,
                    descriptionValue,
                    ticket.maxNumberOfThisSellableTicket,
                    ticket.peoplePerTicket,
                    ticket.graceTimeInSeconds,
                    rawPrice
                ];
                if (requiredTicketFields.some((value) => !String(value || "").trim())) {
                    return false;
                }
                if (parseNumber(rawPrice) === null) {
                    return false;
                }
                return true;
            });
            if (!requiredTicketsValid) {
                toast.error("Bitte füllen Sie alle Pflichtfelder in den KeyTick Tickets aus.");
                return;
            }
        }
        
        let created = false;
        let saved = false;
        try {
            submitInFlightRef.current = true;
            setLoading(true);
            const liveNoticeText = "LIVE! im KUltur-BUnker unter dem Diakonissenplatz Stuttgart";
            const liveNoticeHtml = `<p><strong>${liveNoticeText}</strong></p>`;
            let descriptionWithNotice = sanitizeDescriptionHtml(currentDescriptionValue);
            if (!descriptionWithNotice.includes(liveNoticeText)) {
                descriptionWithNotice = `${descriptionWithNotice || ""}${liveNoticeHtml}`;
            }
            descriptionWithNotice = normalizeEditorLinks(descriptionWithNotice);
            const normalizedCustomCategory = customCategory.trim();
            const normalizedCustomCategoryLower = normalizedCustomCategory.toLowerCase();
            const existingCategory = categories.find(
                (category) => category.toLowerCase() === normalizedCustomCategoryLower
            );
            const resolvedCategory = isCustomCategory && normalizedCustomCategory
                ? (existingCategory || normalizedCustomCategory)
                : formData.category;
            const shouldCreateCategory = Boolean(
                isCustomCategory && normalizedCustomCategory && !existingCategory
            );
            const primarySchedule = isMultiDay ? schedulePayload[0] : null;
            const payload = {
                ...formData,
                short_description: formData.short_description.trim(),
                description: descriptionWithNotice,
                category: resolvedCategory,
                category_is_new: shouldCreateCategory,
                image_url: formData.image_url.trim(),
                cover_image_url: formData.image_url.trim() || null,
                date: primarySchedule?.date || formData.date,
                time: primarySchedule?.time || formData.time,
                door_time: primarySchedule?.door_time || formData.door_time,
                end_time: primarySchedule?.end_time || formData.end_time,
                admission: formData.admission.trim(),
                admin_note: formData.admin_note.trim(),
                homepage_url: formData.homepage_url.trim() || null,
                ticket_url: formData.ticket_url.trim() || null,
                artist_names: (Array.isArray(formData.artist_names) ? formData.artist_names : [])
                    .map((name) => String(name || "").trim())
                    .filter(Boolean),
                artist_name: (Array.isArray(formData.artist_names) ? formData.artist_names : [])
                    .map((name) => String(name || "").trim())
                    .filter(Boolean)
                    .join(", ") || null,
                gema_relevant: Boolean(formData.gema_relevant),
                gema_self_managed: Boolean(formData.gema_relevant && formData.gema_self_managed),
                gema_frei: Boolean(formData.gema_relevant && formData.gema_frei),
                gema_event_type: formData.gema_relevant ? (formData.gema_event_type || null) : null,
                is_setlist_required: Boolean(formData.gema_relevant && isSetlistRequired),
                eintrittsgeld_netto: formData.gema_relevant && formData.eintrittsgeld_netto !== ""
                    ? Number(String(formData.eintrittsgeld_netto).replace(",", "."))
                    : null,
                dj_costs_total: formData.gema_relevant && formData.dj_costs_total !== ""
                    ? Number(String(formData.dj_costs_total).replace(",", "."))
                    : null,
                veranstaltungsraeume: formData.gema_relevant
                    ? (Array.isArray(formData.veranstaltungsraeume) ? formData.veranstaltungsraeume : [])
                    : [],
                besucher_anzahl: formData.gema_relevant && formData.besucher_anzahl !== ""
                    ? Number(formData.besucher_anzahl)
                    : null,
                vinyl_only: Boolean(formData.gema_relevant && formData.vinyl_only),
                ...gemaExtrasToPayload(formData),
                setlist_status: formData.gema_relevant
                    ? (isSetlistRequired
                        ? (formData.setlist_status || (formData.setlist_entries.length > 0 ? "Eingereicht" : "Ausstehend"))
                        : "Nicht notwendig")
                    : "Nicht notwendig",
                setlist_deadline: formData.gema_relevant ? (formData.setlist_deadline || null) : null,
                setlist_entries: formData.gema_relevant
                    ? (isSetlistRequired ? formData.setlist_entries : [])
                        .map((entry) => ({
                            titel: (entry.titel || "").trim(),
                            komponist: (entry.komponist || "").trim(),
                            interpret_original: (entry.interpret_original || "").trim() || null,
                            dauer_sekunden: entry.dauer_sekunden === "" ? null : Number(entry.dauer_sekunden),
                            bearbeiter: (entry.bearbeiter || "").trim() || null,
                            verlag: (entry.verlag || "").trim() || null
                        }))
                        .filter((entry) => entry.titel && entry.komponist)
                    : [],
                pricing_tiers: sanitizedPricingTiers,
                gallery_image_urls: formData.gallery_image_urls.filter((url) => url && url.trim()).map((url) => url.trim()),
                event_dates: isMultiDay ? schedulePayload : []
            };
            const normalizedOrganizerEmail = organizerEmail.trim().toLowerCase();
            if (isAdmin && normalizedOrganizerEmail) {
                const defaultOwner = (eventOwnerEmail || userEmail || "").trim().toLowerCase();
                if (!defaultOwner || normalizedOrganizerEmail !== defaultOwner) {
                    payload.organizer_email = normalizedOrganizerEmail;
                }
            }
            if (keytickEnabled) {
                let tickets = [];
                try {
                    const admissionPrice = parseAdmissionPrice(formData.admission);
                    if (sanitizedPricingTiers.some((tier) => parseAdmissionPrice(tier.price) === null)) {
                        throw new Error("invalid_pricing_tier_price");
                    }
                    tickets = keytickData.tickets.map((ticket, index) => {
                        const priceNettoValue = index === 0 && admissionPrice !== null && !String(ticket.priceNetto || "").trim()
                            ? admissionPrice
                            : ticket.priceNetto;
                        const titleValue = index === 0 ? "Standard-Ticket" : (ticket.title || "").trim();
                        const descriptionValue = (ticket.description || defaultTicketDescription).trim();
                        const mapped = {
                            title: titleValue,
                            description: descriptionValue,
                            maxNumberOfThisSellableTicket: parseInteger(ticket.maxNumberOfThisSellableTicket),
                            peoplePerTicket: parseInteger(ticket.peoplePerTicket),
                            graceTimeInSeconds: parseInteger(ticket.graceTimeInSeconds),
                            priceNetto: parseNumber(priceNettoValue),
                            taxPercent: parseNumber(ticket.taxPercent),
                            taxNote: (ticket.taxNote || "").trim() || null,
                            sellStartTime: ticket.sellStartTime,
                            sellEndTime: ticket.sellEndTime
                        };
                        if (
                            mapped.maxNumberOfThisSellableTicket === null ||
                            mapped.peoplePerTicket === null ||
                            mapped.graceTimeInSeconds === null ||
                            mapped.priceNetto === null
                        ) {
                            throw new Error("invalid_ticket_number");
                        }
                        if (
                            mapped.maxNumberOfThisSellableTicket <= 0 ||
                            mapped.peoplePerTicket <= 0 ||
                            mapped.graceTimeInSeconds < 0 ||
                            mapped.priceNetto < 0
                        ) {
                            throw new Error("invalid_ticket_range");
                        }
                        mapped.graceTimeInSeconds = Math.trunc(mapped.graceTimeInSeconds) * 60;
                        return mapped;
                    });
                } catch (error) {
                    const message = error?.message === "invalid_ticket_range"
                        ? "KeyTick Ticket-Zahlen müssen positiv sein."
                        : error?.message === "invalid_pricing_tier_price"
                            ? "Staffelpreis enthält keinen gültigen Preis."
                            : "KeyTick Ticket-Zahlen sind ungültig.";
                    toast.error(message);
                    setLoading(false);
                    return;
                }
                payload.keytick = {
                    mode: keytickMode,
                    location: "Kulturbunker Stuttgart",
                    requiredAddress: Boolean(keytickData.requiredAddress),
                    eventPassword: keytickData.eventPassword.trim() || null,
                    maximalNumberOfAttendee: parseNumber(keytickData.maximalNumberOfAttendee),
                    retrieveServiceFeeFromCustomer: false,
                    bannerImageBase64: keytickBannerBase64 || null,
                    tickets,
                    email: keytickMode === "mode_b" ? keytickData.email.trim() : null,
                    password: keytickMode === "mode_b" ? keytickData.password : null,
                    organisationId: keytickMode === "mode_b" ? keytickData.organisationId : null
                };
            }
            if (isEditing && eventStatus === "rejected") {
                payload.status = "pending";
            }
            if (!payload.category) {
                toast.error("Bitte wählen Sie eine Kategorie aus.");
                setLoading(false);
                return;
            }

            if (
                isEditing
                && gemaScheduleBaselineRef.current
                && gemaScheduleSignature(payload) !== gemaScheduleBaselineRef.current
                && !window.confirm(
                    "Diese Veranstaltung ist bereits bei der GEMA angemeldet.\n"
                    + "Die Änderung von Datum/Uhrzeit muss auch bei der GEMA beantragt werden "
                    + "(GEMA-Portal → Meine Veranstaltungen → Optionen → „Änderung beantragen“).\n\n"
                    + "Trotzdem speichern?"
                )
            ) {
                setLoading(false);
                return;
            }

            if (isEditing) {
                await apiService.updateEvent(eventId, payload);
                toast.success("Veranstaltung aktualisiert!");
                saved = true;
            } else {
                await apiService.createEvent(payload);
                toast.success("Veranstaltung erfolgreich eingereicht!");
                created = true;
                saved = true;
            }
        } catch (error) {
            console.error("Error creating event:", error);
            const message = error.response?.data?.detail || "Fehler beim Speichern der Veranstaltung";
            toast.error(message);
            if (!isEditing && /smtp|e-mail|email|benachrichtig/i.test(message)) {
                navigate("/my-events");
            }
        } finally {
            submitInFlightRef.current = false;
            setLoading(false);
            if (saved && effectiveReturnPath) {
                navigate(effectiveReturnPath, { replace: true });
                setTimeout(() => {
                    window.location.href = effectiveReturnPath;
                }, 0);
            } else if (created) {
                navigate("/my-events", { replace: true });
                setTimeout(() => {
                    window.location.href = "/my-events";
                }, 0);
            }
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadingImage(true);
            const { url } = await apiService.uploadEventImage(file);
            setFormData(prev => ({
                ...prev,
                image_url: url
            }));
            toast.success("Bild erfolgreich hochgeladen!");
        } catch (error) {
            console.error("Error uploading image:", error);
            const message = error.response?.data?.detail || "Bild-Upload fehlgeschlagen";
            toast.error(message);
        } finally {
            setUploadingImage(false);
            event.target.value = "";
        }
    };

    const handleGalleryUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) {
            return;
        }

        const uploadedUrls = [];
        try {
            setUploadingGallery(true);
            for (const file of files) {
                const { url } = await apiService.uploadEventImage(file);
                uploadedUrls.push(url);
            }

            if (uploadedUrls.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    gallery_image_urls: [...prev.gallery_image_urls, ...uploadedUrls]
                }));
                toast.success(`${uploadedUrls.length} Bild(er) hochgeladen!`);
            }
        } catch (error) {
            console.error("Error uploading gallery images:", error);
            const message = error.response?.data?.detail || "Bild-Upload fehlgeschlagen";
            toast.error(message);
        } finally {
            setUploadingGallery(false);
            event.target.value = "";
        }
    };

    const imagePreviewUrl = useMemo(() => {
        if (!formData.image_url) {
            return null;
        }
        if (formData.image_url.startsWith("http")) {
            return formData.image_url;
        }
        if (process.env.REACT_APP_BACKEND_URL) {
            return `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`;
        }
        return formData.image_url;
    }, [formData.image_url]);
    
    if (initialLoading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
            </div>
        );
    }
    
    const statusConfig = {
        pending: { label: "Ausstehend", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
        approved: { label: "Genehmigt", className: "bg-green-100 text-green-800 border-green-200" },
        published: { label: "Veröffentlicht", className: "bg-purple-100 text-purple-800 border-purple-200" },
        rejected: { label: "Abgelehnt", className: "bg-red-100 text-red-800 border-red-200" }
    };
    const currentStatus = statusConfig[eventStatus] || null;

    if (isReadOnly && isEditing) {
        return (
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900">Veranstaltung ansehen</h2>
                        </div>
                        <div className="flex items-center gap-3">
                            {adminEditLink && (
                                <Link
                                    to={adminEditLink}
                                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 p-2 text-gray-700 hover:bg-gray-50"
                                    aria-label="Veranstaltung bearbeiten"
                                    title="Veranstaltung bearbeiten"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L12 15l-4 1 1-4 8.586-8.586z" />
                                    </svg>
                                </Link>
                            )}
                            {currentStatus && (
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${currentStatus.className}`}>
                                    {currentStatus.label}
                                </span>
                            )}
                        </div>
                    </div>

                    {rejectionMessage && eventStatus === "rejected" && (
                        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-sm font-semibold text-red-800">Deine Veranstaltung wurde abgelehnt.</p>
                            <p className="text-sm text-red-700 mt-2">{rejectionMessage}</p>
                        </div>
                    )}

                    {imagePreviewUrl && (
                        <div className="mb-6">
                            <img
                                src={imagePreviewUrl}
                                alt={formData.title || "Veranstaltung"}
                                className="w-full h-64 object-cover rounded-xl border border-gray-200"
                            />
                        </div>
                    )}

                    <div className="space-y-6">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{formData.title || "Ohne Titel"}</h3>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                {isMultiDay && Array.isArray(formData.event_dates) && formData.event_dates.length > 0 ? (
                                    formData.event_dates.map((entry, index) => (
                                        <span key={`preview-date-${index}`}>
                                            {entry.date ? formatDate(entry.date) : "Datum offen"}{" "}
                                            {entry.time || "Zeit offen"}
                                            {entry.end_time ? ` bis ${entry.end_time}` : ""}
                                        </span>
                                    ))
                                ) : (
                                    <>
                                        <span>{formData.date ? formatDate(formData.date) : "Datum offen"}</span>
                                        <span>{formData.time || "Zeit offen"}</span>
                                        {formData.end_time && <span>bis {formData.end_time}</span>}
                                    </>
                                )}
                                {formData.category && <span>{decodeHtml(formData.category)}</span>}
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-sm font-semibold text-gray-900 mb-2">Kurzbeschreibung</p>
                            <p className="text-gray-700 leading-relaxed break-words">
                                {formData.short_description || "Keine Kurzbeschreibung vorhanden."}
                            </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2 text-sm">
                            <div className="rounded-lg border border-gray-200 p-4">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Eintritt</p>
                                <p className="text-gray-900 font-semibold">{formData.admission || "—"}</p>
                                {formData.door_time && (
                                    <p className="text-gray-600 text-xs mt-2">Einlass: {formData.door_time}</p>
                                )}
                            </div>
                            <div className="rounded-lg border border-gray-200 p-4">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Hinweise</p>
                                <p className="text-gray-700">
                                    {formData.is_kultur_for_all ? "Kultur für alle" : "Standard"}
                                </p>
                                {formData.sold_out && (
                                    <p className="text-red-600 text-xs mt-2">Ausverkauft</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold text-gray-900 mb-2">Beschreibung</p>
                            <div
                                className="text-gray-700 leading-relaxed break-words"
                                dangerouslySetInnerHTML={{
                                    __html: normalizeHtml(replaceSeparatorMarkersForEditor(formData.description || "<p>Keine Beschreibung vorhanden.</p>"))
                                }}
                            />
                        </div>

                        {(formData.homepage_url || formData.ticket_url) && (
                            <div className="flex flex-wrap gap-4 text-sm text-purple-700">
                                {formData.homepage_url && (
                                    <a href={formData.homepage_url} target="_blank" rel="noreferrer" className="hover:underline">
                                        Homepage
                                    </a>
                                )}
                                {formData.ticket_url && (
                                    <a href={formData.ticket_url} target="_blank" rel="noreferrer" className="hover:underline">
                                        Tickets
                                    </a>
                                )}
                            </div>
                        )}

                        {Array.isArray(formData.pricing_tiers) && formData.pricing_tiers.length > 0 && (
                            <div className="text-sm text-gray-700">
                                <p className="font-semibold text-gray-900">Staffelpreise</p>
                                <ul className="mt-2 space-y-1">
                                    {formData.pricing_tiers.map((tier, index) => (
                                        <li key={index}>
                                            <span className="font-medium">{tier.label || "Preis"}:</span> {tier.price || "—"}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {formData.admin_note && (
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                                <p className="text-sm font-semibold text-blue-800">Info an Admin</p>
                                <p className="text-sm text-blue-700 mt-2">{formData.admin_note}</p>
                            </div>
                        )}

                        {Array.isArray(formData.gallery_image_urls) && formData.gallery_image_urls.length > 0 && (
                            <div>
                                <p className="text-sm font-semibold text-gray-900 mb-2">Weitere Bilder</p>
                                <div className="flex flex-wrap gap-3">
                                    {formData.gallery_image_urls.map((url, index) => (
                                        <img
                                            key={`${url}-${index}`}
                                            src={resolveImageUrl(url)}
                                            alt={`Galeriebild ${index + 1}`}
                                            className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-8 flex justify-end">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Zurück
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <h2 className="text-3xl font-bold text-gray-900">
                        {isEditing ? "Veranstaltung bearbeiten" : "Neue Veranstaltung melden"}
                    </h2>
                    {isEditing && (
                        <Link
                            to="/my-events"
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Meine Meldungen
                        </Link>
                    )}
                </div>

                {rejectionMessage && eventStatus === "rejected" && (
                    <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
                        <p className="text-sm font-semibold text-red-800">Deine Veranstaltung wurde abgelehnt.</p>
                        <p className="text-sm text-red-700 mt-2">{rejectionMessage}</p>
                        <p className="text-xs text-red-700 mt-3">
                            Bitte passe die Angaben an und speichere erneut, um die Veranstaltung wieder einzureichen.
                        </p>
                    </div>
                )}
                
                <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">Basisdaten</h3>
                    </div>
                    <div>
                        <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                            Titel <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            data-testid="input-title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                            placeholder="Name der Veranstaltung"
                        />
                    </div>

                    <div>
                        <label htmlFor="short_description" className="block text-sm font-semibold text-gray-700 mb-2">
                            Kurzbeschreibung <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="short_description"
                            name="short_description"
                            data-testid="input-short-description"
                            value={formData.short_description}
                            onChange={handleChange}
                            required
                            maxLength={140}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                            placeholder="Kurzbeschreibung für den Flyer"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Wird für den Flyer benötigt. Max. 140 Zeichen ({formData.short_description.length}/140).
                        </p>
                    </div>
                    
                    {isAdmin && (
                        <div>
                            <label htmlFor="organizer" className="block text-sm font-semibold text-gray-700 mb-2">
                                Veranstalter zuweisen
                            </label>
                            <select
                                id="organizer"
                                value={organizerEmail}
                                onChange={(event) => setOrganizerEmail(event.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                            >
                                <option value="">Aktueller Admin (keine Zuweisung)</option>
                                {organizerEmail && !organizers.some((item) => item.email?.toLowerCase() === organizerEmail) && (
                                    <option value={organizerEmail}>
                                        Aktueller Besitzer ({organizerEmail})
                                    </option>
                                )}
                                {organizers.map((organizer) => (
                                    <option key={organizer.user_id} value={organizer.email?.toLowerCase()}>
                                        {organizer.name || organizer.email}
                                    </option>
                                ))}
                            </select>
                            {organizersLoading && (
                                <p className="text-xs text-gray-500 mt-2">Veranstalter werden geladen...</p>
                            )}
                        </div>
                    )}
                    
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">Terminplanung</h3>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={isMultiDay}
                                onChange={(event) => handleMultiDayToggle(event.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span>
                                Mehrtägige Veranstaltung
                                <span className="block text-xs text-gray-500 mt-1">
                                    Für jeden Tag einen eigenen Termin hinzufügen.
                                </span>
                            </span>
                        </label>

                        {!isMultiDay && (
                            <>
                                <div className={`grid gap-6 ${keytickMode === "none" ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                                    <div>
                                        <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Datum <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="date"
                                            name="date"
                                            data-testid="input-date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label htmlFor="time" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Startzeit <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="time"
                                            id="time"
                                            name="time"
                                            data-testid="input-time"
                                            value={formData.time}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="door_time" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Einlasszeit
                                    </label>
                                    <input
                                        type="time"
                                        id="door_time"
                                        name="door_time"
                                        data-testid="input-door-time"
                                        value={formData.door_time}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="end_time" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Endzeit (Datum & Uhrzeit)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="end_time"
                                        name="end_time"
                                        data-testid="input-end-time"
                                        value={formData.end_time}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </>
                        )}

                        {isMultiDay && (
                            <div className="space-y-4">
                                {(Array.isArray(formData.event_dates) ? formData.event_dates : []).map((entry, index) => (
                                    <div key={`event-date-${index}`} className="grid gap-4 md:grid-cols-4 items-end border border-gray-200 rounded-lg bg-white p-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Datum <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={entry.date || ""}
                                                onChange={(event) => updateScheduleEntry(index, "date", event.target.value)}
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Startzeit <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={entry.time || ""}
                                                onChange={(event) => updateScheduleEntry(index, "time", event.target.value)}
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Einlasszeit
                                            </label>
                                            <input
                                                type="time"
                                                value={entry.door_time || ""}
                                                onChange={(event) => updateScheduleEntry(index, "door_time", event.target.value)}
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Endzeit
                                            </label>
                                            <input
                                                type="time"
                                                value={entry.end_time || ""}
                                                onChange={(event) => updateScheduleEntry(index, "end_time", event.target.value)}
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div className="md:col-span-4 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => removeScheduleEntry(index)}
                                                className="text-xs font-semibold text-red-600 hover:text-red-700"
                                            >
                                                Termin entfernen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addScheduleEntry}
                                    className="inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
                                >
                                    Termin hinzufügen
                                </button>
                                {(Array.isArray(formData.event_dates) ? formData.event_dates : []).length === 0 && (
                                    <p className="text-xs text-gray-500">Noch keine Termine angelegt.</p>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                                Kategorie <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="category"
                                name="category"
                                data-testid="input-category"
                                value={formData.category}
                                onChange={handleChange}
                                required
                                disabled={isCustomCategory}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                            {categoryLoading && (
                                <p className="text-xs text-gray-500 mt-2">Kategorien werden geladen...</p>
                            )}
                            {!isCustomCategory && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsCustomCategory(true);
                                        setFormData(prev => ({ ...prev, category: "" }));
                                    }}
                                    className="mt-3 inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
                                >
                                    Neue Kategorie anlegen
                                </button>
                            )}
                            {isCustomCategory && (
                                <div className="mt-3 space-y-2">
                                    <input
                                        type="text"
                                        value={customCategory}
                                        onChange={(event) => setCustomCategory(event.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        placeholder="Neue Kategorie eingeben"
                                    />
                                    {categoryExists && (
                                        <p className="text-xs text-red-600">
                                            Hinweis: Diese Kategorie existiert bereits.
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsCustomCategory(false);
                                            setCustomCategory("");
                                            setFormData(prev => ({
                                                ...prev,
                                                category: categories[0] || ""
                                            }));
                                        }}
                                        className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                                    >
                                        Zurück zur Liste
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <label htmlFor="admission" className="block text-sm font-semibold text-gray-700 mb-2">
                                Eintritt <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="admission"
                                name="admission"
                                data-testid="input-admission"
                                value={formData.admission}
                                onChange={handleChange}
                                required
                                maxLength={40}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                placeholder="z.B. 10€, Frei, Spende"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <span className="text-sm font-semibold text-gray-700">Staffelpreise (optional)</span>
                                <p className="text-xs text-gray-500 mt-1">
                                    Lege zusätzliche Preisstufen an, z. B. für ermäßigte Tickets oder Gruppenpreise.
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="add-pricing-tier"
                                onClick={addPricingTier}
                                className="inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
                            >
                                Staffelpreis hinzufügen
                            </button>
                        </div>

                        {formData.pricing_tiers.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                Noch keine Staffelpreise angelegt.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {formData.pricing_tiers.map((tier, index) => (
                                    <div
                                        key={index}
                                        className="grid md:grid-cols-[2fr,1fr,auto] gap-4 bg-white border border-gray-200 rounded-lg p-4"
                                    >
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                Bezeichnung
                                            </label>
                                            <input
                                                type="text"
                                                value={tier.label}
                                                data-testid={`pricing-tier-label-${index}`}
                                                onChange={(event) => handlePricingTierChange(index, "label", event.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                placeholder="z. B. Ermäßigt, Gruppe ab 10 Personen"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                Preis
                                            </label>
                                            <input
                                                type="text"
                                                value={tier.price}
                                                data-testid={`pricing-tier-price-${index}`}
                                                onChange={(event) => handlePricingTierChange(index, "price", event.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                placeholder="z. B. 5 €"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                data-testid={`remove-pricing-tier-${index}`}
                                                onClick={() => removePricingTier(index)}
                                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700"
                                            >
                                                Entfernen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3">
                        <h3 className="text-sm font-semibold text-indigo-900">GEMA & Setlist</h3>
                    </div>

                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-5 space-y-4">
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                id="gema_relevant"
                                name="gema_relevant"
                                checked={formData.gema_relevant}
                                onChange={handleCheckboxChange}
                                className="mt-1 h-5 w-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <div>
                                <label htmlFor="gema_relevant" className="text-sm font-semibold text-indigo-900">
                                    GEMA-relevant (Musik ist geplant)
                                </label>
                                <p className="text-xs text-indigo-700 mt-1">
                                    Aktiviere diese Option, um GEMA-Daten und Setlist zu verwalten.
                                </p>
                            </div>
                        </div>

                        {formData.gema_relevant && (
                            <div className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-white px-3 py-3">
                                <input
                                    type="checkbox"
                                    id="gema_self_managed"
                                    name="gema_self_managed"
                                    checked={formData.gema_self_managed}
                                    onChange={handleCheckboxChange}
                                    className="mt-1 h-5 w-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                    <label htmlFor="gema_self_managed" className="text-sm font-semibold text-indigo-900">
                                        Ich melde die GEMA selbst (Eigengema)
                                    </label>
                                    <p className="text-xs text-indigo-700 mt-1">
                                        Der Veranstalter meldet und bezahlt die GEMA eigenständig.
                                    </p>
                                </div>
                            </div>
                        )}

                        {formData.gema_relevant && (
                            <div className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-white px-3 py-3">
                                <input
                                    type="checkbox"
                                    id="gema_frei"
                                    name="gema_frei"
                                    checked={formData.gema_frei}
                                    onChange={handleCheckboxChange}
                                    className="mt-1 h-5 w-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                    <label htmlFor="gema_frei" className="text-sm font-semibold text-indigo-900">
                                        Es wird nur GEMA-freie Musik gespielt
                                    </label>
                                    <p className="text-xs text-indigo-700 mt-1">
                                        Z. B. eigene Stücke von Künstler:innen ohne GEMA-Mitgliedschaft, gemeinfreie Werke
                                        oder lizenzfreie Musik. Keine GEMA-Anmeldung und keine Setlist nötig.
                                    </p>
                                </div>
                            </div>
                        )}

                        {formData.gema_relevant && formData.gema_self_managed && (
                            <div className="rounded-lg border border-indigo-200 bg-white px-4 py-3 text-sm text-gray-600">
                                Da die GEMA eigenständig gemeldet wird, sind keine weiteren GEMA-Angaben (Tarif,
                                Räume, Besucherzahl, Setlist) notwendig.
                            </div>
                        )}

                        {formData.gema_relevant && formData.gema_frei && (
                            <div className="rounded-lg border border-indigo-200 bg-white px-4 py-3 text-sm text-gray-600">
                                Bei ausschließlich GEMA-freier Musik sind keine weiteren GEMA-Angaben (Tarif, Räume,
                                Besucherzahl, Setlist) notwendig. Wird doch GEMA-Repertoire gespielt, bitte den Haken entfernen.
                            </div>
                        )}

                        {formData.gema_relevant && !formData.gema_self_managed && !formData.gema_frei && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-gray-800">Was findet statt?</p>
                                    {inferredGemaTypeFromCategory && (
                                        <p className="text-xs text-indigo-700">
                                            Vorschlag aufgrund der Kategorie:{" "}
                                            {resolveGemaTypeMeta(inferredGemaTypeFromCategory)?.title || inferredGemaTypeFromCategory}
                                        </p>
                                    )}
                                    {!inferredGemaTypeFromCategory && (
                                        <p className="text-xs text-gray-500">
                                            Kein automatischer Vorschlag aus Titel/Kategorie erkannt.
                                        </p>
                                    )}
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {GEMA_EVENT_TYPES.map((option) => {
                                            const selected = formData.gema_event_type === option.value;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => {
                                                        lastAutoGemaTypeRef.current = "";
                                                        setFormData((prev) => ({ ...prev, gema_event_type: option.value }));
                                                    }}
                                                    className={`rounded-lg border px-4 py-3 text-left transition ${
                                                        selected
                                                            ? "border-indigo-500 bg-indigo-100 shadow-sm"
                                                            : "border-gray-200 bg-white hover:border-indigo-300"
                                                    }`}
                                                >
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {option.title}
                                                    </p>
                                                    <p className="mt-1 text-xs text-gray-600">{option.subtext}</p>
                                                    <p className="mt-1 text-xs text-indigo-700">Tarif: {option.tariff}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {gemaTarifVorschlag && (
                                    <div className="rounded-lg border border-indigo-200 bg-white px-4 py-3 text-sm">
                                        <p className="font-semibold text-indigo-900">Tarifvorschlag: {gemaTarifVorschlag}</p>
                                        <p className="text-gray-600 mt-1">
                                            {isSetlistRequired ? "Setlist Pflicht: Ja" : "Setlist Pflicht: Nein"}
                                        </p>
                                        {gemaTarifVorschlag === "M-SP" && gemaEintrittNettoWert !== null && gemaEintrittNettoWert > 10 && (
                                            <p className="mt-1 text-red-700">
                                                Hohe GEMA-Kosten möglich! Bitte Eintrittspreis prüfen (ggf. Kursgebühr splitten).
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="grid gap-4 md:grid-cols-3">
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Eintrittsgeld netto (€) *</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            name="eintrittsgeld_netto"
                                            value={formData.eintrittsgeld_netto}
                                            onChange={handleChange}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            required={formData.gema_relevant}
                                        />
                                        <span className="mt-1 block text-xs text-gray-500">Bei Spende hier bitte 0 eintragen</span>
                                    </label>
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Gesamtkosten der Künstler (€) *</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            name="dj_costs_total"
                                            value={formData.dj_costs_total}
                                            onChange={handleChange}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            required={formData.gema_relevant}
                                        />
                                    </label>
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Räume *</span>
                                        <div className="space-y-2 rounded-lg border border-gray-300 px-3 py-2">
                                            {GEMA_ROOM_OPTIONS.map((room) => (
                                                <label key={room} className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.veranstaltungsraeume || []).includes(room)}
                                                        onChange={() => toggleVeranstaltungsraum(room)}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span>{room}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </label>
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Erwartete Besucher *</span>
                                        <input
                                            type="number"
                                            min="0"
                                            name="besucher_anzahl"
                                            value={formData.besucher_anzahl}
                                            onChange={handleChange}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            required={formData.gema_relevant}
                                        />
                                    </label>
                                </div>

                                <div className="rounded-lg border border-indigo-100 bg-white p-4">
                                    <p className="text-sm font-semibold text-gray-900">Weitere Angaben für die GEMA</p>
                                    <p className="mb-3 text-xs text-gray-500">
                                        Fragt die GEMA je nach Tarif ab. Vorgaben nur ändern, wenn es zutrifft.
                                    </p>
                                    <div className="grid gap-4 md:grid-cols-3">
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">Pausen ohne Musik (Minuten)</span>
                                            <input type="number" min="0" step="1" name="gema_pausen_ohne_musik_min" value={formData.gema_pausen_ohne_musik_min} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">Sponsoring / geldwerte Vorteile</span>
                                            <select name="gema_sponsoring" value={formData.gema_sponsoring} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                <option value="nein">Nein</option>
                                                <option value="ja">Ja</option>
                                            </select>
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">Zusätzliche Musik / Pausenmusik</span>
                                            <select name="gema_pausenmusik" value={formData.gema_pausenmusik} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                <option value="NEIN">Nein</option>
                                                <option value="BIS_50%">Ja, bis 50 % der Zeit</option>
                                                <option value="UEBER_50%">Ja, über 50 % der Zeit</option>
                                            </select>
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">TV-Wiedergaben</span>
                                            <select name="gema_tv_wiedergabe" value={formData.gema_tv_wiedergabe} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                <option value="NEIN">Nein</option>
                                                <option value="OHNEVG">Ja (nur ARD/ZDF/Dritte)</option>
                                                <option value="MITVG">Ja (auch Privatsender)</option>
                                            </select>
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">Tonträgereinlage</span>
                                            <select name="gema_tontraegereinlage" value={formData.gema_tontraegereinlage} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                <option value="nein">Nein</option>
                                                <option value="ja">Ja</option>
                                            </select>
                                        </label>
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-2 font-medium">Bands / DJs</span>
                                        <div className="space-y-2">
                                            {(formData.artist_names || [""]).map((name, index) => (
                                                <div key={`artist-${index}`} className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={name}
                                                        onChange={(event) => updateArtistName(index, event.target.value)}
                                                        placeholder={`Band/DJ ${index + 1}`}
                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeArtistName(index)}
                                                        className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                                                    >
                                                        Entfernen
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={addArtistName}
                                                className="rounded-lg border border-indigo-200 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                            >
                                                Weitere Band/DJ hinzufügen
                                            </button>
                                        </div>
                                    </label>
                                </div>

                                <label className="flex items-start gap-3 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        name="vinyl_only"
                                        checked={formData.vinyl_only}
                                        onChange={handleCheckboxChange}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span>Es wird ausschließlich mit Original-Schallplatten/CDs aufgelegt (VR-Ö Zuschlag entfällt).</span>
                                </label>

                                {isSetlistRequired ? (
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">Setlist-Verwaltung</p>
                                                <p className="text-xs text-gray-500">
                                                    Status: {formData.setlist_status || "Ausstehend"} | Deadline: {formData.setlist_deadline ? formatDate(formData.setlist_deadline) : "-"}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={addSetlistEntry}
                                                className="rounded-lg border border-indigo-200 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                            >
                                                Song hinzufügen
                                            </button>
                                        </div>
                                        <label className="text-sm text-gray-700">
                                            <span className="block mb-1 font-medium">Setlist-Status</span>
                                            <select
                                                name="setlist_status"
                                                value={formData.setlist_status}
                                                onChange={handleChange}
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                            >
                                                <option value="Ausstehend">Ausstehend</option>
                                                <option value="Eingereicht">Eingereicht</option>
                                            </select>
                                        </label>

                                        {(formData.setlist_entries || []).length === 0 ? (
                                            <p className="text-sm text-gray-500">Noch keine Songs erfasst.</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {(formData.setlist_entries || []).map((entry, index) => (
                                                    <div key={`setlist-${index}`} className="grid gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-2">
                                                        <input
                                                            type="text"
                                                            value={entry.titel || ""}
                                                            onChange={(event) => updateSetlistEntry(index, "titel", event.target.value)}
                                                            placeholder="Titel *"
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={entry.komponist || ""}
                                                            onChange={(event) => updateSetlistEntry(index, "komponist", event.target.value)}
                                                            placeholder="Komponist *"
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={entry.interpret_original || ""}
                                                            onChange={(event) => updateSetlistEntry(index, "interpret_original", event.target.value)}
                                                            placeholder="Original-Interpret"
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                        />
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={entry.dauer_sekunden || ""}
                                                            onChange={(event) => updateSetlistEntry(index, "dauer_sekunden", event.target.value)}
                                                            placeholder="Dauer (Sek.)"
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={entry.bearbeiter || ""}
                                                            onChange={(event) => updateSetlistEntry(index, "bearbeiter", event.target.value)}
                                                            placeholder="Bearbeiter"
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                        />
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={entry.verlag || ""}
                                                                onChange={(event) => updateSetlistEntry(index, "verlag", event.target.value)}
                                                                placeholder="Verlag"
                                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeSetlistEntry(index)}
                                                                className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                                                            >
                                                                Entfernen
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
                                        Für diesen Tarif ist keine Setlist notwendig.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">Medien</h3>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label htmlFor="event-image-file" className="block text-sm font-semibold text-gray-700 mb-2">
                                Titelbild hochladen
                            </label>
                            <input
                                type="file"
                                id="event-image-file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                disabled={uploadingImage}
                                className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer disabled:opacity-60"
                            />
                            {uploadingImage && (
                                <p className="text-sm text-purple-600 mt-2">Bild wird hochgeladen...</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                                Unterstützte Formate: JPG, PNG, GIF, WEBP (max. {process.env.REACT_APP_MAX_UPLOAD_SIZE_MB || 5} MB)
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                Empfehlung für WordPress Titelbild: 2000x1200 px.
                            </p>
                        </div>

                        {!imagePreviewUrl && (
                            <p className="text-sm text-gray-500">
                                Bitte lade ein Titelbild hoch.
                            </p>
                        )}

                        {imagePreviewUrl && (
                            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-4">
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Titelbild Vorschau</p>
                                    <img
                                        src={imagePreviewUrl}
                                        alt="Event Vorschau"
                                        className="w-full h-64 object-cover rounded-md shadow-sm"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                                    className="inline-flex text-sm text-red-600 hover:text-red-700"
                                >
                                    Titelbild entfernen
                                </button>
                            </div>
                        )}
                        {requiresImportConfirm && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
                                <p>
                                    Bilder für WordPress-Importe können ohne Bestätigung gespeichert werden.
                                    Alle anderen Änderungen erfordern weiterhin die Bestätigung.
                                </p>
                                <button
                                    type="button"
                                    onClick={submitImageOnlyUpdate}
                                    disabled={loading || uploadingImage || uploadingGallery}
                                    className="inline-flex items-center justify-center rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                >
                                    Nur Bilder speichern
                                </button>
                            </div>
                        )}
                        <div className="border-t border-gray-200 pt-4 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <span className="text-sm font-semibold text-gray-700">Weitere Bilder</span>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Lade zusätzliche Fotos hoch.
                                    </p>
                                </div>
                                <label className="inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors cursor-pointer">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleGalleryUpload}
                                        disabled={uploadingGallery}
                                        className="hidden"
                                    />
                                    {uploadingGallery ? "Lädt..." : "Bilder wählen"}
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Empfehlung für WordPress Galerie: 1600x900 px.
                            </p>

                            {formData.gallery_image_urls.length > 0 && (
                                <div className="space-y-3">
                                    <p className="text-sm font-semibold text-gray-700">Weitere Bilder</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {formData.gallery_image_urls.map((url, index) => (
                                            <div key={url + index} className="relative border border-gray-200 rounded-lg overflow-hidden">
                                                <img
                                                    src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
                                                    alt={`Galerie Bild ${index + 1}`}
                                                    className="w-full h-40 object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveGalleryImage(index)}
                                                    className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded"
                                                >
                                                    Entfernen
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <input
                            type="checkbox"
                            id="sold_out"
                            name="sold_out"
                            data-testid="input-sold-out"
                            checked={formData.sold_out}
                            onChange={handleCheckboxChange}
                            className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div>
                            <label htmlFor="sold_out" className="text-sm font-semibold text-gray-700">
                                Ausverkauft
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                                Markiere die Veranstaltung als ausverkauft.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">Beschreibung</h3>
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                            Beschreibung
                        </label>
                        <RichTextEditor
                            key={`${eventId || duplicateFrom || "new"}-${descriptionEditorVersion}-${isReadOnly ? "ro" : "rw"}`}
                            initialValue={formData.description}
                            onBlur={handleDescriptionBlur}
                            placeholder="Beschreibung der Veranstaltung"
                            readOnly={isReadOnly}
                            editorInstanceRef={descriptionEditorRef}
                            editorId={`description-${eventId || duplicateFrom || "new"}-${descriptionEditorVersion}`}
                        />
                    </div>

                    <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <input
                            type="checkbox"
                            id="is_kultur_for_all"
                            name="is_kultur_for_all"
                            data-testid="input-kultur-for-all"
                            checked={formData.is_kultur_for_all}
                            onChange={handleCheckboxChange}
                            className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div>
                            <label htmlFor="is_kultur_for_all" className="text-sm font-semibold text-gray-700">
                                Teilnahme an "Kultur für alle"
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                                Aktivieren Sie diese Option, wenn Ihre Veranstaltung im Rahmen des "Kultur für alle"-Programms stattfindet.
                            </p>
                        </div>
                    </div>

                    <div className={`grid gap-6 ${keytickMode === "none" || keytickExisting?.eventId ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                        {keytickMode === "none" && (
                            <div>
                                <label htmlFor="ticket_url" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Ticketlink (optional)
                                </label>
                                <input
                                    type="url"
                                    id="ticket_url"
                                    name="ticket_url"
                                    data-testid="input-ticket-url"
                                    value={formData.ticket_url}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="https://tickets.mein-verein.de"
                                />
                            </div>
                        )}
                        {keytickMode !== "none" && keytickExisting?.eventId && (
                            <div>
                                <label htmlFor="ticket_url" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Ticketlink
                                </label>
                                <input
                                    type="url"
                                    id="ticket_url"
                                    name="ticket_url"
                                    data-testid="input-ticket-url"
                                    value={keytickExisting.eventUrl || formData.ticket_url}
                                    disabled
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Wird automatisch von KeyTick verwaltet und kann nicht bearbeitet werden.
                                </p>
                            </div>
                        )}

                        <div>
                            <label htmlFor="homepage_url" className="block text-sm font-semibold text-gray-700 mb-2">
                                Homepage
                            </label>
                            <input
                                type="url"
                                id="homepage_url"
                                name="homepage_url"
                                data-testid="input-homepage-url"
                                value={formData.homepage_url}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                placeholder="https://mein-verein.de"
                            />
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">KeyTick</h3>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="space-y-1">
                                <p className="text-sm font-semibold text-gray-900">KeyTick Integration (optional)</p>
                                <p className="text-xs text-gray-500">
                                    Lege Tickets direkt in KeyTick an oder arbeite wie bisher nur mit Ticketlink.
                                </p>
                            </div>
                            <img
                                src="https://keytick.com/dashboard/Logo/keytick-logo.svg"
                                alt="KeyTick"
                                className="h-8 w-auto"
                                loading="lazy"
                            />
                        </div>

                        {keytickExisting?.eventId && (
                            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                                KeyTick wurde bereits angelegt.
                                {keytickExisting.eventUrl ? (
                                    <a
                                        href={keytickExisting.eventUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ml-2 underline text-green-700"
                                    >
                                        Event öffnen
                                    </a>
                                ) : (
                                    <span className="ml-2">
                                        Event-ID: {keytickExisting.eventId} (Link nicht konfiguriert)
                                    </span>
                                )}
                            </div>
                        )}
                        {isEditing && !keytickExisting?.eventId && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                                KeyTick kann nur beim Erstellen einer Veranstaltung angelegt werden.
                            </div>
                        )}

                        <div>
                            <label htmlFor="keytick-mode" className="block text-sm font-semibold text-gray-700 mb-2">
                                KeyTick Modus
                            </label>
                            <select
                                id="keytick-mode"
                                value={keytickMode}
                                onChange={(event) => {
                                    setKeytickMode(event.target.value);
                                    setKeytickOrganisations([]);
                                    setKeytickData(prev => ({ ...prev, organisationId: "" }));
                                }}
                                disabled={Boolean(keytickExisting?.eventId) || isEditing}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                            >
                                <option value="none">Kein KeyTick</option>
                                <option value="mode_a">Auf KuBu KeyTick anlegen</option>
                                <option value="mode_b">Mit KeyTick Account anmelden</option>
                            </select>
                        </div>

                        {keytickMode !== "none" && !keytickExisting?.eventId && (
                            <div className="space-y-5">
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                                    Für KeyTick brauchst du Endzeit und ein Titelbild. Bitte im Formular ergänzen.
                                </div>

                                {keytickMode === "mode_b" && (
                                    <div className="space-y-4">
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="keytick-email" className="block text-sm font-semibold text-gray-700 mb-2">
                                                    KeyTick E-Mail
                                                </label>
                                                <input
                                                    type="email"
                                                    id="keytick-email"
                                                    name="email"
                                                    value={keytickData.email}
                                                    onChange={handleKeytickChange}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                    placeholder="name@example.com"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="keytick-password" className="block text-sm font-semibold text-gray-700 mb-2">
                                                    KeyTick Passwort
                                                </label>
                                                <input
                                                    type="password"
                                                    id="keytick-password"
                                                    name="password"
                                                    value={keytickData.password}
                                                    onChange={handleKeytickChange}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={loadKeytickOrganisations}
                                                disabled={keytickOrgLoading}
                                                className="inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-60"
                                            >
                                                {keytickOrgLoading ? "Lade Organisationen..." : "Organisationen laden"}
                                            </button>
                                            {keytickOrganisations.length > 0 && (
                                                <select
                                                    value={keytickData.organisationId}
                                                    onChange={(event) => setKeytickData(prev => ({ ...prev, organisationId: event.target.value }))}
                                                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                >
                                                    <option value="">Organisation auswählen</option>
                                                    {keytickOrganisations.map((org) => (
                                                        <option key={org.organisationId} value={org.organisationId}>
                                                            {org.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="keytick-max-attendees" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Max. Teilnehmende *
                                    </label>
                                    <input
                                        type="number"
                                        id="keytick-max-attendees"
                                        name="maximalNumberOfAttendee"
                                        value={keytickData.maximalNumberOfAttendee}
                                        onChange={handleKeytickChange}
                                        required={keytickMode !== "none" && !keytickExisting?.eventId}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        min="1"
                                        placeholder="z. B. 120"
                                    />
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="keytick-event-password" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Event-Passwort (optional)
                                        </label>
                                        <input
                                            type="text"
                                            id="keytick-event-password"
                                            name="eventPassword"
                                            value={keytickData.eventPassword}
                                            onChange={handleKeytickChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                            placeholder="z. B. secret123"
                                        />
                                    </div>
                                    <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <input
                                            type="checkbox"
                                            id="keytick-required-address"
                                            name="requiredAddress"
                                            checked={keytickData.requiredAddress}
                                            onChange={handleKeytickChange}
                                            className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        />
                                        <div>
                                            <label htmlFor="keytick-required-address" className="text-sm font-semibold text-gray-700">
                                                Adresse der Käufer erforderlich
                                            </label>
                                            <p className="text-xs text-gray-500 mt-1">
                                                KeyTick erfordert diese Angabe als Pflichtfeld.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                                    <div>
                                        <label htmlFor="keytick-banner" className="block text-sm font-semibold text-gray-700 mb-2">
                                            KeyTick Banner (optional)
                                        </label>
                                        <input
                                            type="file"
                                            id="keytick-banner"
                                            accept="image/*"
                                            onChange={handleKeytickBannerUpload}
                                            className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                                        />
                                        <p className="text-xs text-gray-500 mt-2">
                                            Bitte lade ein Bild für dein Event hoch. Optimales Format: 2000x1200.
                                        </p>
                                    </div>
                                    {keytickBannerPreview ? (
                                        <div>
                                            <p className="text-xs font-semibold text-gray-700 mb-2">KeyTick Vorschau</p>
                                            <img
                                                src={keytickBannerPreview}
                                                alt="KeyTick Banner Vorschau"
                                                className="w-full h-48 object-cover rounded-lg border border-gray-200"
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500">
                                            Falls leer, wird das Titelbild der Veranstaltung verwendet.
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-4 border border-gray-200 rounded-xl p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <p className="text-sm font-semibold text-gray-900">KeyTick Tickets (mindestens 1)</p>
                                        <button
                                            type="button"
                                            onClick={addKeytickTicket}
                                            className="inline-flex items-center justify-center rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
                                        >
                                            Ticket hinzufügen
                                        </button>
                                    </div>

                                    {keytickData.tickets.map((ticket, index) => (
                                        <div key={`keytick-ticket-${index}`} className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <p className="text-sm font-semibold text-gray-800">
                                                    Ticket {index + 1}
                                                </p>
                                                {index > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeKeytickTicket(index)}
                                                        className="text-sm font-medium text-red-600 hover:text-red-700"
                                                    >
                                                        Entfernen
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Ticket-Titel
                                                    </label>
                                                    {index === 0 ? (
                                                        <input
                                                            type="text"
                                                            value="Standard-Ticket"
                                                            disabled
                                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={ticket.title}
                                                            onChange={(event) => handleKeytickTicketChange(index, "title", event.target.value)}
                                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            placeholder="z. B. Early Bird"
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Ticket-Beschreibung
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={ticket.description}
                                                        onChange={(event) => handleKeytickTicketChange(index, "description", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. Zugang zur Veranstaltung"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Max. Ticketanzahl
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ticket.maxNumberOfThisSellableTicket}
                                                        onChange={(event) => handleKeytickTicketChange(index, "maxNumberOfThisSellableTicket", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. 200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Personen pro Ticket
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ticket.peoplePerTicket}
                                                        onChange={(event) => handleKeytickTicketChange(index, "peoplePerTicket", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Einlass früher (Min.)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={ticket.graceTimeInSeconds}
                                                        onChange={(event) => handleKeytickTicketChange(index, "graceTimeInSeconds", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. 30"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Preis (EUR)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={ticket.priceNetto}
                                                        onChange={(event) => handleKeytickTicketChange(index, "priceNetto", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. 10"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Steuersatz (optional)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={ticket.taxPercent}
                                                        onChange={(event) => handleKeytickTicketChange(index, "taxPercent", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. 19"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Steuerhinweis (optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={ticket.taxNote}
                                                        onChange={(event) => handleKeytickTicketChange(index, "taxNote", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        placeholder="z. B. Standard VAT rate"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Verkaufsstart (optional)
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        value={normalizeDateTimeLocal(ticket.sellStartTime, formData.date)}
                                                        onChange={(event) => handleKeytickTicketChange(index, "sellStartTime", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">Leer lassen = jetzt.</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Verkaufsende (optional)
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        value={normalizeDateTimeLocal(ticket.sellEndTime, formData.date)}
                                                        onChange={(event) => handleKeytickTicketChange(index, "sellEndTime", event.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">Leer lassen = Eventende.</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="admin_note" className="block text-sm font-semibold text-gray-700 mb-2">
                            Info an Admin
                        </label>
                        <textarea
                            id="admin_note"
                            name="admin_note"
                            data-testid="input-admin-note"
                            value={formData.admin_note}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                            placeholder="Interne Hinweise für die Freigabe..."
                        />
                    </div>
                    
                    {!isEditing && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                <strong>Hinweis:</strong> Ihre Veranstaltung wird nach der Einreichung von einem Administrator überprüft und freigegeben.
                            </p>
                        </div>
                    )}
                    {requiresImportConfirm && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                            <p className="text-sm text-amber-900">
                                <strong>Warnung WordPress-Import:</strong> Diese Veranstaltung wurde importiert.
                                Bei Änderungen kann nicht sichergestellt werden, dass das auf der Webseite nicht brickt.
                                Fahre nur fort, wenn du weißt, was du tust.
                            </p>
                            <label className="flex items-start gap-3 text-sm text-amber-900">
                                <input
                                    type="checkbox"
                                    checked={importEditConfirmed}
                                    onChange={(event) => setImportEditConfirmed(event.target.checked)}
                                    className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span>Ich verstehe das Risiko und möchte fortfahren.</span>
                            </label>
                            <label className="text-sm text-amber-900">
                                <span className="block mb-1 font-medium">
                                    Tippe: "{importEditPassphrase}"
                                </span>
                                <input
                                    type="text"
                                    value={importEditPhrase}
                                    onChange={(event) => setImportEditPhrase(event.target.value)}
                                    placeholder={importEditPassphrase}
                                    className="w-full rounded-lg border border-amber-200 px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                                />
                            </label>
                        </div>
                    )}
                    {isAdmin && isEditing && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-purple-100 bg-purple-50 p-4">
                            <div>
                                <p className="text-sm font-semibold text-purple-900">WordPress-Aktionen</p>
                                <p className="text-xs text-purple-700">
                                    Verknüpfte Veranstaltungen können direkt synchronisiert oder zurückgeholt werden.
                                </p>
                                {wordpressUrl && (
                                    <a
                                        href={wordpressUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex text-xs font-medium text-purple-700 underline underline-offset-2"
                                    >
                                        WordPress-Beitrag öffnen
                                    </a>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                {!hasWordpressConnection && (eventStatus === "approved" || eventStatus === "published") && (
                                    <button
                                        type="button"
                                        onClick={handleWordpressPublish}
                                        disabled={syncingWordpress || recallingWordpress}
                                        className="inline-flex items-center justify-center rounded-lg border border-purple-200 bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 transition-colors disabled:opacity-50"
                                    >
                                        {syncingWordpress ? "Veröffentliche..." : "Zu WordPress veröffentlichen"}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleWordpressSync}
                                    disabled={syncingWordpress || recallingWordpress || !hasWordpressConnection}
                                    className="inline-flex items-center justify-center rounded-lg border border-purple-200 bg-white px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-50"
                                >
                                    {syncingWordpress ? "Sync läuft..." : "Sync to WordPress"}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleWordpressRecall}
                                    disabled={syncingWordpress || recallingWordpress || !hasWordpressConnection}
                                    className="inline-flex items-center justify-center rounded-lg border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
                                >
                                    {recallingWordpress ? "Rückruf läuft..." : "Von WordPress zurückholen"}
                                </button>
                            </div>
                        </div>
                    )}
                    {isAdmin && isEditing && (wordpressPublishError || (!hasWordpressConnection && eventStatus === "approved")) && (
                        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-900">
                            <p className="text-sm font-semibold">Nicht auf WordPress veröffentlicht</p>
                            <p className="mt-1 break-words text-xs">
                                {wordpressPublishError || "Der automatische WordPress-Push war nicht erfolgreich. Für diesen älteren Fehler sind keine Details gespeichert."}
                            </p>
                            {wordpressPublishFailedAt && (
                                <p className="mt-1 text-xs text-red-700">
                                    Letzter Versuch: {new Date(wordpressPublishFailedAt).toLocaleString("de-DE")}
                                </p>
                            )}
                        </div>
                    )}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            data-testid="submit-event-button"
                            disabled={loading || isReadOnly || !importEditUnlocked}
                            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {loading
                                ? (isEditing ? "Speichern..." : "Wird eingereicht...")
                                : (isEditing ? "Änderungen speichern" : "Veranstaltung einreichen")}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="px-6 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Abbrechen
                        </button>
                    </div>
                </form>
            </div>
            {showNoGemaConfirmModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 px-4">
                    <div className="w-full max-w-2xl rounded-2xl border border-red-300 bg-red-50 p-6 shadow-2xl">
                        <h3 className="text-2xl font-bold text-red-900">Achtung: GEMA ist deaktiviert</h3>
                        <p className="mt-3 text-red-800">
                            Bist du dir sicher, dass auf dieser Veranstaltung keine Musik läuft?
                            Ansonsten aktiviere bitte die GEMA.
                        </p>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowNoGemaConfirmModal(false);
                                    setNoGemaConfirmed(false);
                                    noGemaConfirmBypassRef.current = false;
                                }}
                                className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-800 hover:bg-red-100"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    noGemaConfirmBypassRef.current = true;
                                    setNoGemaConfirmed(true);
                                    setShowNoGemaConfirmModal(false);
                                    formRef.current?.requestSubmit();
                                }}
                                className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
                            >
                                Ja, ohne GEMA speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateEvent;
